import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import io from 'socket.io-client';
import './GroceryQrForm.css';

const apiUrl = process.env.REACT_APP_API_URL || 'https://vakra.co.in/api';
const socketUrl = process.env.REACT_APP_WEBSOCKET_URL || 'https://vakra.co.in';

const DEFAULT_MOBILE_NUMBER = '9999999999';
const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/400x400?text=No+Image';

const STEP_LANDING = 1;
const STEP_MODE = 2;
const STEP_CUSTOMER = 3;
const STEP_PRODUCTS = 4;
const STEP_REVIEW = 5;
const STEP_PAYMENT = 6;
const STEP_SUCCESS = 7;
const STEP_TRACK = 8;

const TRACKING_STEPS = [
  { key: 'pending', label: 'Order Placed', shortLabel: 'Placed', help: 'Order shop ko mil gaya' },
  { key: 'accepted', label: 'Accepted', shortLabel: 'Accepted', help: 'Shop ne order accept kiya' },
  { key: 'packing', label: 'Packing', shortLabel: 'Packing', help: 'Order pack ho raha hai' },
  { key: 'ready', label: 'Ready', shortLabel: 'Ready', help: 'Order ready hai' },
  { key: 'done', label: 'Completed', shortLabel: 'Done', help: 'Order complete ho gaya' },
];

const formatMoney = (value) => `₹${Number(value || 0).toFixed(2)}`;
const normalizeMobile = (value) => String(value || '').replace(/\D/g, '').slice(0, 10);
const safeArray = (value) => (Array.isArray(value) ? value : []);

const safeFlag = (value, fallback = 0) => {
  if (value === true) return 1;
  if (value === false) return 0;

  if (value === 1 || value === 0) return Number(value);

  const text = String(value ?? '').trim().toLowerCase();

  if (['1', 'true', 'yes', 'on', 'enabled', 'active'].includes(text)) return 1;
  if (['0', 'false', 'no', 'off', 'disabled', 'inactive'].includes(text)) return 0;

  return fallback;
};

const safeNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const normalizeOrderSettings = (raw = {}) => {
  const data = raw?.settings || raw?.data || raw || {};

  return {
    enable_pickup: safeFlag(data.enable_pickup, 1),
    enable_home_delivery: safeFlag(data.enable_home_delivery, 0),

    allow_cod: safeFlag(data.allow_cod, 1),
    allow_online_payment: safeFlag(data.allow_online_payment, 1),

    min_order_amount: safeNumber(data.min_order_amount, 0),
    delivery_fee: safeNumber(data.delivery_fee, 0),
    packaging_fee: safeNumber(data.packaging_fee, 0),
    delivery_radius_km: safeNumber(data.delivery_radius_km, 0),
    delivery_eta_min: safeNumber(data.delivery_eta_min, 30),
    delivery_eta_max: safeNumber(data.delivery_eta_max, 60),

    shop_open_time: data.shop_open_time || data.shopOpenTime || '',
    shop_close_time: data.shop_close_time || data.shopCloseTime || '',
  };
};

const parseShopTimeToMinutes = (value) => {
  if (!value) return null;

  const text = String(value).trim();
  if (!text) return null;

  const timeOnly = text.includes('T')
    ? text.split('T')[1]?.slice(0, 5)
    : text.slice(0, 5);

  const match = String(timeOnly || '').match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

  return hour * 60 + minute;
};

const formatShopTime = (value) => {
  const minutes = parseShopTimeToMinutes(value);
  if (minutes == null) return '';

  const hour24 = Math.floor(minutes / 60);
  const minute = minutes % 60;

  const suffix = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;

  return `${hour12}:${String(minute).padStart(2, '0')} ${suffix}`;
};

const getShopTimingText = (settings = {}) => {
  const open = formatShopTime(settings.shop_open_time || settings.shopOpenTime);
  const close = formatShopTime(settings.shop_close_time || settings.shopCloseTime);

  if (open && close) return `${open} - ${close}`;
  if (open) return `Open: ${open}`;
  if (close) return `Close: ${close}`;

  return 'Not set';
};

const getShopOpenStatus = (settings = {}) => {
  const openMinutes = parseShopTimeToMinutes(settings.shop_open_time || settings.shopOpenTime);
  const closeMinutes = parseShopTimeToMinutes(settings.shop_close_time || settings.shopCloseTime);

  if (openMinutes == null || closeMinutes == null) {
    return {
      isOpen: true,
      label: 'Timing Not Set',
      message: 'Shop timing set nahi hai, isliye order allowed hai.',
    };
  }

  if (openMinutes === closeMinutes) {
    return {
      isOpen: true,
      label: 'Open 24 Hours',
      message: 'Shop 24 hours open hai.',
    };
  }

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  let isOpen = false;

  if (openMinutes < closeMinutes) {
    isOpen = nowMinutes >= openMinutes && nowMinutes <= closeMinutes;
  } else {
    isOpen = nowMinutes >= openMinutes || nowMinutes <= closeMinutes;
  }

  return {
    isOpen,
    label: isOpen ? 'Open Now' : 'Closed Now',
    message: isOpen
      ? `Shop open hai. Timing: ${getShopTimingText(settings)}`
      : `Shop abhi closed hai. Order ${getShopTimingText(settings)} ke time par place kar sakte hain.`,
  };
};

const safeParseProducts = (value) => {
  try {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'object') return value;
    if (typeof value === 'string') return JSON.parse(value);
    return [];
  } catch {
    return [];
  }
};

const normalizeStatusText = (value) => {
  const text = String(value || '').trim();
  if (!text) return '-';
  return text.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeTrackingStatus = (value) => {
  const status = String(value || '').toLowerCase().trim();

  if (!status) return 'pending';
  if (status === 'preparing') return 'packing';
  if (status === 'packed') return 'packing';
  if (status === 'complete') return 'done';
  if (status === 'completed') return 'done';
  if (status === 'delivered') return 'done';
  if (status === 'cancel') return 'cancelled';
  if (status === 'canceled') return 'cancelled';

  if (['pending', 'accepted', 'packing', 'ready', 'done', 'cancelled'].includes(status)) {
    return status;
  }

  return 'pending';
};

const getLatestTrackingStatus = (order) => {
  const logs = Array.isArray(order?.logs) ? order.logs : [];

  if (logs.length > 0) {
    const latestLog = logs[logs.length - 1];

    return normalizeTrackingStatus(
      latestLog.new_status ||
        latestLog.status ||
        latestLog.displayStatus ||
        order?.status
    );
  }

  return normalizeTrackingStatus(order?.status);
};

const getTrackingIndex = (status) => {
  const safeStatus = normalizeTrackingStatus(status);

  if (safeStatus === 'cancelled') return -1;

  const foundIndex = TRACKING_STEPS.findIndex((step) => step.key === safeStatus);
  return foundIndex >= 0 ? foundIndex : 0;
};

const buildDisplayLogs = (order) => {
  const logs = Array.isArray(order?.logs) ? order.logs : [];

  if (logs.length > 0) {
    return logs.map((log, index) => ({
      id: log.id || `${log.new_status || log.status || 'log'}-${index}`,
      status: normalizeTrackingStatus(log.new_status || log.status),
      label: normalizeStatusText(log.new_status || log.status),
      note: log.note || '',
      created_at: log.created_at || log.createdAt || '',
    }));
  }

  return [
    {
      id: 'fallback-pending',
      status: normalizeTrackingStatus(order?.status || 'pending'),
      label: normalizeStatusText(order?.status || 'pending'),
      note: 'Order placed',
      created_at: order?.created_at || order?.createdAt || '',
    },
  ];
};


const parseCoordinate = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const isValidLatitude = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num >= -90 && num <= 90;
};

const isValidLongitude = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num >= -180 && num <= 180;
};

const isUsableCoordinatePair = (lat, lng) => {
  const finalLat = Number(lat);
  const finalLng = Number(lng);

  if (!isValidLatitude(finalLat) || !isValidLongitude(finalLng)) {
    return false;
  }

  if (finalLat === 0 && finalLng === 0) {
    return false;
  }

  return true;
};

const getFirstUsableCoordinatePair = (...pairs) => {
  for (const pair of pairs) {
    if (!Array.isArray(pair) || pair.length < 2) continue;

    const lat = parseCoordinate(pair[0]);
    const lng = parseCoordinate(pair[1]);

    if (isUsableCoordinatePair(lat, lng)) {
      return { latitude: lat, longitude: lng };
    }
  }

  return { latitude: null, longitude: null };
};

const normalizePublicProfilePayload = (raw = {}) => {
  const payload =
    raw?.profile ||
    raw?.vendor ||
    raw?.user ||
    raw?.data ||
    raw ||
    {};

  const coords = getFirstUsableCoordinatePair(
    [payload.shop_latitude, payload.shop_longitude],
    [payload.shopLatitude, payload.shopLongitude],
    [payload.shop_lat, payload.shop_lng],
    [payload.shop_lat, payload.shop_lon],
    [payload.latitude, payload.longitude],
    [payload.lat, payload.lng],
    [payload.lat, payload.lon]
  );

  return {
    ...payload,
    shop_latitude: coords.latitude,
    shop_longitude: coords.longitude,
    shopLatitude: coords.latitude,
    shopLongitude: coords.longitude,
  };
};

const calculateDistanceKm = (lat1, lon1, lat2, lon2) => {
  const fromLat = Number(lat1);
  const fromLon = Number(lon1);
  const toLat = Number(lat2);
  const toLon = Number(lon2);

  if (!isUsableCoordinatePair(fromLat, fromLon) || !isUsableCoordinatePair(toLat, toLon)) {
    console.warn('Invalid coordinate pair for distance calculation:', {
      fromLat,
      fromLon,
      toLat,
      toLon,
    });
    return null;
  }

  const toRad = (value) => (Number(value) * Math.PI) / 180;
  const earthRadiusKm = 6371;

  const dLat = toRad(toLat - fromLat);
  const dLon = toRad(toLon - fromLon);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(fromLat)) *
      Math.cos(toRad(toLat)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return earthRadiusKm * c;
};

const getAddressPart = (address = {}, keys = []) => {
  for (const key of keys) {
    if (address[key]) return address[key];
  }

  return '';
};

const reverseGeocodeCustomerLocation = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
        lat
      )}&lon=${encodeURIComponent(lng)}`
    );

    if (!response.ok) return null;

    const data = await response.json();
    const address = data.address || {};

    const house = getAddressPart(address, ['house_number', 'building', 'amenity']);
    const road = getAddressPart(address, ['road', 'neighbourhood', 'suburb']);
    const area = getAddressPart(address, [
      'suburb',
      'neighbourhood',
      'quarter',
      'village',
      'town',
      'city_district',
    ]);
    const finalCity = getAddressPart(address, ['city', 'town', 'village', 'municipality', 'county']);
    const finalState = address.state || '';
    const finalPincode = String(address.postcode || '').replace(/\D/g, '').slice(0, 6);

    return {
      displayName: data.display_name || '',
      addressLine1: [house, road].filter(Boolean).join(', '),
      addressLine2: area,
      city: finalCity,
      state: finalState,
      pincode: finalPincode,
    };
  } catch (error) {
    console.error('Reverse geocode failed:', error);
    return null;
  }
};

const GroceryQrForm = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const vendorId = queryParams.get('vendorId') || '';
  const businessType = (queryParams.get('businessType') || 'grocery').toLowerCase();
  const groceryMode = (queryParams.get('groceryMode') || queryParams.get('mode') || 'retail').toLowerCase();
  const qrName = queryParams.get('qrName') || '';
  const table = queryParams.get('table') || '';

  const paymentDismissTimerRef = useRef(null);
  const socketRef = useRef(null);
  const lookupTimerRef = useRef(null);

  const [step, setStep] = useState(STEP_LANDING);

  const [loading, setLoading] = useState(true);
  const [qrAccess, setQrAccess] = useState({
    checked: false,
    isActive: true,
    message: '',
  });
  const [placingOrder, setPlacingOrder] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paidOnline, setPaidOnline] = useState(false);
  const [verifiedPaymentData, setVerifiedPaymentData] = useState(null);
  const [isPaymentVerifying, setIsPaymentVerifying] = useState(false);
  const [paymentStatusMessage, setPaymentStatusMessage] = useState('');

  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [vendorProfile, setVendorProfile] = useState({
    shop_name: '',
    shopName: '',
    shop_image: '',
    shopImage: '',
    shop_logo: '',
    live_location: '',
    liveLocation: '',
    shop_latitude: null,
    shop_longitude: null,
    shopLatitude: null,
    shopLongitude: null,
    latitude: null,
    longitude: null,
    number: '',
    mobile: '',
    phone: '',
    contact: '',
  });

  const [allProducts, setAllProducts] = useState([]);
  const [offers, setOffers] = useState([]);

  const [orderSettings, setOrderSettings] = useState({
    enable_pickup: 1,
    enable_home_delivery: 0,
    allow_cod: 1,
    allow_online_payment: 1,
    min_order_amount: 0,
    delivery_fee: 0,
    packaging_fee: 0,
    delivery_radius_km: 0,
    delivery_eta_min: 30,
    delivery_eta_max: 60,
    shop_open_time: '',
    shop_close_time: '',
  });

  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showTrendingOnly, setShowTrendingOnly] = useState(false);

  const [cart, setCart] = useState([]);
  const [orderMode, setOrderMode] = useState('pickup');
  const [paymentMode, setPaymentMode] = useState('cash');

  const [mobileNumber, setMobileNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [landmark, setLandmark] = useState('');
  const [city, setCity] = useState('');
  const [stateName, setStateName] = useState('');
  const [pincode, setPincode] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [customerLatitude, setCustomerLatitude] = useState('');
  const [customerLongitude, setCustomerLongitude] = useState('');
  const [customerLocationAccuracy, setCustomerLocationAccuracy] = useState(null);
  const [customerLocationLoading, setCustomerLocationLoading] = useState(false);
  const [customerLocationMessage, setCustomerLocationMessage] = useState('');
  const [deliveryDistanceKm, setDeliveryDistanceKm] = useState(null);

  const [customerLookupLoading, setCustomerLookupLoading] = useState(false);
  const [customerFound, setCustomerFound] = useState(false);
  const [customerLookupMessage, setCustomerLookupMessage] = useState('');

  const [trackMobile, setTrackMobile] = useState('');
  const [trackLoading, setTrackLoading] = useState(false);
  const [todayOrders, setTodayOrders] = useState([]);

  const [pricingPreview, setPricingPreview] = useState({
    products: [],
    baseTotal: 0,
    extraCharges: 0,
    discountAmount: 0,
    finalTotal: 0,
    validatedOfferName: null,
    appliedOffer: null,
    deliveryEtaMinutes: null,
  });

  const [placedOrder, setPlacedOrder] = useState(null);
  const [showClaimInput, setShowClaimInput] = useState(false);
  const [manualRedeemCode, setManualRedeemCode] = useState('');

  const [contactProduct, setContactProduct] = useState(null);

  const shopName =
    vendorProfile.shop_name ||
    vendorProfile.shopName ||
    vendorProfile.name ||
    'Grocery Store';

  const shopImage =
    vendorProfile.shop_logo ||
    vendorProfile.shop_image ||
    vendorProfile.shopImage ||
    '';

  const shopLocation =
    vendorProfile.live_location ||
    vendorProfile.liveLocation ||
    '';

  const vendorNumber =
    vendorProfile.number ||
    vendorProfile.mobile ||
    vendorProfile.phone ||
    vendorProfile.contact ||
    '';

  const shopCoordinatePair = useMemo(() => {
    return getFirstUsableCoordinatePair(
      [vendorProfile.shop_latitude, vendorProfile.shop_longitude],
      [vendorProfile.shopLatitude, vendorProfile.shopLongitude],
      [vendorProfile.shop_lat, vendorProfile.shop_lng],
      [vendorProfile.shop_lat, vendorProfile.shop_lon],
      [vendorProfile.latitude, vendorProfile.longitude],
      [vendorProfile.lat, vendorProfile.lng],
      [vendorProfile.lat, vendorProfile.lon]
    );
  }, [vendorProfile]);

  const shopLatitude = shopCoordinatePair.latitude;
  const shopLongitude = shopCoordinatePair.longitude;
  const deliveryRadiusKm = Number(orderSettings.delivery_radius_km || 0);

  const hasShopCoordinates = isUsableCoordinatePair(shopLatitude, shopLongitude);
  const hasCustomerCoordinates = isUsableCoordinatePair(
    customerLatitude,
    customerLongitude
  );

  const deliveryRangeStatus = useMemo(() => {
    if (orderMode !== 'delivery') {
      return {
        checked: false,
        allowed: true,
        distanceKm: null,
        message: '',
      };
    }

    if (!deliveryRadiusKm || deliveryRadiusKm <= 0) {
      return {
        checked: false,
        allowed: true,
        distanceKm: null,
        message: 'Shop ne delivery radius set nahi kiya hai.',
      };
    }

    if (!hasShopCoordinates) {
      return {
        checked: false,
        allowed: false,
        distanceKm: null,
        message: 'Shop location set nahi hai. Vendor se contact karein.',
      };
    }

    if (!hasCustomerCoordinates) {
      return {
        checked: false,
        allowed: false,
        distanceKm: null,
        message: `Delivery ke liye location allow karein. Shop ${deliveryRadiusKm} KM radius me delivery karta hai.`,
      };
    }

    const distance = calculateDistanceKm(
      shopLatitude,
      shopLongitude,
      customerLatitude,
      customerLongitude
    );

    if (distance === null) {
      return {
        checked: false,
        allowed: false,
        distanceKm: null,
        message: 'Distance calculate nahi ho paya. Location dobara allow karein.',
      };
    }

    const allowed = distance <= deliveryRadiusKm;

    return {
      checked: true,
      allowed,
      distanceKm: distance,
      message: allowed
        ? `Delivery available. Aap shop se approx ${distance.toFixed(2)} KM door hain.`
        : `Delivery available nahi hai. Aap approx ${distance.toFixed(
            2
          )} KM door hain, shop sirf ${deliveryRadiusKm} KM tak delivery karta hai.`,
    };
  }, [
    orderMode,
    deliveryRadiusKm,
    hasShopCoordinates,
    hasCustomerCoordinates,
    shopLatitude,
    shopLongitude,
    customerLatitude,
    customerLongitude,
  ]);

  const isContactPriceItem = (item = {}) => {
    const pricingType = String(item.pricing_type || item.pricingType || '').toLowerCase();
    const showPrice = Number(item.show_price ?? item.showPrice ?? 1);
    const sellingPrice = Number(item.selling_price ?? item.sellingPrice ?? 0);

    return (
      pricingType === 'inquiry' ||
      pricingType === 'hidden' ||
      showPrice === 0 ||
      !Number.isFinite(sellingPrice) ||
      sellingPrice <= 0
    );
  };

  const hasContactPriceItems = useMemo(() => {
    return cart.some((item) => Number(item.quantity || 0) > 0 && isContactPriceItem(item));
  }, [cart]);

  useEffect(() => {
    fetchInitialData();
  }, [vendorId, groceryMode]);

  useEffect(() => {
    if (!vendorId || businessType !== 'grocery') return;

    const socket = io(socketUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      query: {
        token: localStorage.getItem('token') || 'anonymous',
      },
    });

    socketRef.current = socket;

    const applyStockUpdates = (payload) => {
      if (!payload) return;

      const updates = Array.isArray(payload.updates) ? payload.updates : [payload];

      const relevant = updates.filter((item) => {
        const sameVendor = Number(item.vendorId || payload.vendorId || 0) === Number(vendorId);
        const sameMode =
          String(item.groceryMode || payload.groceryMode || '').toLowerCase() ===
          String(groceryMode || '').toLowerCase();

        return sameVendor && sameMode;
      });

      if (!relevant.length) return;

      setAllProducts((prev) =>
        prev.map((product) => {
          const found = relevant.find((u) => Number(u.productId) === Number(product.id));
          if (!found) return product;

          return {
            ...product,
            stock_quantity: Number(found.stock_quantity || 0),
            is_active: found.is_active == null ? product.is_active : Number(found.is_active),
          };
        })
      );

      setCart((prev) =>
        prev
          .map((item) => {
            const found = relevant.find((u) => Number(u.productId) === Number(item.productId));
            if (!found) return item;

            const liveStock = Number(found.stock_quantity || 0);
            const nextQty = Math.min(Number(item.quantity || 0), liveStock);

            if (liveStock <= 0 || nextQty <= 0) return null;

            return {
              ...item,
              stock_quantity: liveStock,
              quantity: nextQty,
            };
          })
          .filter(Boolean)
      );

      setMessage('Stock updated live.');
    };

    socket.on('groceryStockUpdated', applyStockUpdates);
    socket.on('stockUpdated', applyStockUpdates);

    return () => {
      socket.off('groceryStockUpdated', applyStockUpdates);
      socket.off('stockUpdated', applyStockUpdates);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [vendorId, groceryMode, businessType]);

  useEffect(() => {
    const safeSettings = normalizeOrderSettings(orderSettings);

    const pickupEnabled = safeFlag(safeSettings.enable_pickup, 1) === 1;
    const deliveryEnabled = safeFlag(safeSettings.enable_home_delivery, 0) === 1;

    const codAllowed = safeFlag(safeSettings.allow_cod, 1) === 1;
    const onlineAllowed = safeFlag(safeSettings.allow_online_payment, 1) === 1;

    if (!pickupEnabled && deliveryEnabled) {
      setOrderMode('delivery');
    } else if (pickupEnabled && !deliveryEnabled) {
      setOrderMode('pickup');
    }

    setPaymentMode((current) => {
      if (codAllowed && onlineAllowed) {
        return current === 'online' || current === 'cash' ? current : 'cash';
      }

      if (!codAllowed && onlineAllowed) {
        return 'online';
      }

      if (codAllowed && !onlineAllowed) {
        return 'cash';
      }

      return 'cash';
    });
  }, [orderSettings]);

  useEffect(() => {
    if (hasContactPriceItems && paymentMode === 'online') {
      setPaymentMode('cash');
    }
  }, [hasContactPriceItems, paymentMode]);

  useEffect(() => {
    if (cart.length > 0) {
      fetchPricingPreview();
    } else {
      setPricingPreview({
        products: [],
        baseTotal: 0,
        extraCharges: 0,
        discountAmount: 0,
        finalTotal: 0,
        validatedOfferName: null,
        appliedOffer: null,
        deliveryEtaMinutes: null,
        hasContactPriceItems: false,
      });
    }
  }, [cart, orderMode, orderSettings]);

  useEffect(() => {
    const mobile = normalizeMobile(mobileNumber);

    if (lookupTimerRef.current) {
      clearTimeout(lookupTimerRef.current);
    }

    if (step !== STEP_CUSTOMER || mobile.length !== 10) {
      if (mobile.length !== 10) {
        setCustomerFound(false);
        setCustomerLookupMessage('');
      }
      return;
    }

    lookupTimerRef.current = setTimeout(() => {
      fetchCustomerLookup(mobile);
    }, 450);

    return () => {
      if (lookupTimerRef.current) {
        clearTimeout(lookupTimerRef.current);
      }
    };
  }, [mobileNumber, step, vendorId, groceryMode]);

  useEffect(() => {
    return () => {
      clearPaymentDismissTimer();

      if (lookupTimerRef.current) {
        clearTimeout(lookupTimerRef.current);
      }
    };
  }, []);

  const clearPaymentDismissTimer = () => {
    if (paymentDismissTimerRef.current) {
      clearTimeout(paymentDismissTimerRef.current);
      paymentDismissTimerRef.current = null;
    }
  };

  const clearMessages = () => {
    setMessage('');
    setErrorMessage('');
    setPaymentStatusMessage('');
  };

  const getQrStatusParams = () => ({
    vendorId: Number(vendorId),
    vendorType: 'grocery',
    businessType: 'grocery',
    groceryMode,
    mode: groceryMode,
    table: table || '',
    tableNumber: table || '',
    table_number: table || '',
    qrName: qrName || '',
    qr_name: qrName || '',
  });

  const checkQrActiveStatus = async () => {
    if (!vendorId || businessType !== 'grocery') {
      return {
        isActive: false,
        message: 'Invalid grocery QR.',
      };
    }

    try {
      const response = await axios.get(`${apiUrl}/public/grocery/qr-status`, {
        params: getQrStatusParams(),
      });

      const data = response.data || {};
      const isActive =
        data.isActive !== false &&
        Number(data.is_active ?? data.isActive ?? 1) !== 0 &&
        data.qrInactive !== true;

      return {
        isActive,
        message:
          data.message ||
          (isActive
            ? ''
            : 'This QR code is not active. Please contact the shop.'),
      };
    } catch (err) {
      if (err?.response?.status === 403 || err?.response?.data?.qrInactive) {
        return {
          isActive: false,
          message:
            err.response?.data?.message ||
            err.response?.data?.error ||
            'This QR code is not active. Please contact the shop.',
        };
      }

      throw err;
    }
  };

  const renderInactiveQrPage = () => (
    <div
      className="grocery-qr-wrapper qr-inactive-wrapper"
      style={{
        backgroundImage: shopImage ? `url(${shopImage})` : 'none',
      }}
    >
      <div className="grocery-qr-container">
        <div className="grocery-landing-card qr-inactive-card">
          <div
            style={{
              width: 58,
              height: 58,
              borderRadius: 18,
              display: 'grid',
              placeItems: 'center',
              margin: '0 auto 14px',
              background: '#fff1f2',
              color: '#e11d48',
              fontSize: 30,
              fontWeight: 900,
            }}
          >
            !
          </div>

          <span className="grocery-chip">QR Checkout Off</span>
          <h2>QR Code Not Active</h2>
          <p>
            {qrAccess.message ||
              'This QR code is not active. Please contact the shop.'}
          </p>

          {table ? <small>QR: {table}</small> : null}
          {qrName ? <small>QR Name: {qrName}</small> : null}
        </div>
      </div>
    </div>
  );

  const fetchInitialData = async () => {
    if (!vendorId || businessType !== 'grocery') {
      setErrorMessage('Invalid grocery QR.');
      setLoading(false);
      return;
    }

    setLoading(true);
    clearMessages();
    setQrAccess({
      checked: false,
      isActive: true,
      message: '',
    });

    try {
      const qrStatus = await checkQrActiveStatus();

      if (!qrStatus.isActive) {
        setQrAccess({
          checked: true,
          isActive: false,
          message:
            qrStatus.message ||
            'This QR code is not active. Please contact the shop.',
        });

        setAllProducts([]);
        setOffers([]);
        setCart([]);
        setErrorMessage('');
        setLoading(false);
        return;
      }

      setQrAccess({
        checked: true,
        isActive: true,
        message: '',
      });

      const [profileRes, productsRes, settingsRes, offersRes] = await Promise.all([
        axios.get(`${apiUrl}/public/grocery/public-profile`, {
          params: { vendorId, groceryMode },
        }),
        axios.get(`${apiUrl}/public/grocery/products`, {
          params: { vendorId, groceryMode },
        }),
        axios.get(`${apiUrl}/public/grocery-order-settings`, {
          params: { vendorId, mode: groceryMode },
        }),
        axios.get(`${apiUrl}/public/grocery-offers`, {
          params: { vendorId, mode: groceryMode },
        }),
      ]);

      setVendorProfile(normalizePublicProfilePayload(profileRes.data || {}));
      setAllProducts(safeArray(productsRes.data?.products));
      setOrderSettings(normalizeOrderSettings(settingsRes.data || {}));
      setOffers(safeArray(offersRes.data?.offers));
    } catch (err) {
      const msg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to load grocery data.';

      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerLookup = async (mobile) => {
    if (!vendorId || !mobile || mobile.length !== 10) return;

    try {
      setCustomerLookupLoading(true);
      setCustomerLookupMessage('');

      const response = await axios.get(`${apiUrl}/public/grocery/customer-lookup`, {
        params: {
          vendorId,
          groceryMode,
          mobile,
        },
      });

      const customer = response.data?.customer || null;

      if (response.data?.exists && customer) {
        setCustomerFound(true);
        setCustomerLookupMessage('Same mobile mila. Details auto-fill ho gayi.');

        setCustomerName(customer.name || customer.customer_name || customer.customerName || '');
        setAddressLine1(customer.address_line1 || customer.addressLine1 || customer.address || '');
        setAddressLine2(customer.address_line2 || customer.addressLine2 || '');
        setLandmark(customer.landmark || '');
        setCity(customer.city || '');
        setStateName(customer.state || customer.stateName || '');
        setPincode(customer.pincode || '');
        setDeliveryNotes(customer.notes || customer.delivery_notes || '');

        const savedLatitude =
          customer.latitude ||
          customer.customer_latitude ||
          customer.delivery_details?.latitude ||
          customer.delivery_details?.customer_latitude ||
          '';

        const savedLongitude =
          customer.longitude ||
          customer.customer_longitude ||
          customer.delivery_details?.longitude ||
          customer.delivery_details?.customer_longitude ||
          '';

        if (isUsableCoordinatePair(savedLatitude, savedLongitude)) {
          setCustomerLatitude(String(savedLatitude));
          setCustomerLongitude(String(savedLongitude));
        }
      } else {
        setCustomerFound(false);
        setCustomerLookupMessage('New customer. Details fill kar dijiye, next time auto-fill ho jayega.');
      }
    } catch (err) {
      setCustomerFound(false);
      setCustomerLookupMessage('Customer lookup nahi hua. New customer ki tarah details fill kar sakte hain.');
    } finally {
      setCustomerLookupLoading(false);
    }
  };

  const handleUseCustomerLocation = () => {
    if (!navigator.geolocation) {
      setCustomerLocationMessage('Aapke browser/device me location support nahi hai.');
      return;
    }

    setCustomerLocationLoading(true);
    setCustomerLocationMessage('Customer location detect ho rahi hai...');
    setErrorMessage('');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = Number(position.coords.latitude).toFixed(8);
        const lng = Number(position.coords.longitude).toFixed(8);
        const accuracy = Number(position.coords.accuracy || 0);

        setCustomerLatitude(lat);
        setCustomerLongitude(lng);
        setCustomerLocationAccuracy(accuracy || null);

        const distance = hasShopCoordinates
          ? calculateDistanceKm(shopLatitude, shopLongitude, lat, lng)
          : null;

        setDeliveryDistanceKm(distance);

        if (distance !== null && deliveryRadiusKm > 0 && distance > deliveryRadiusKm) {
          setCustomerLocationMessage(
            `Location detect ho gayi, lekin delivery range ke bahar hai. Distance ${distance.toFixed(
              2
            )} KM hai. Shop sirf ${deliveryRadiusKm} KM tak delivery karta hai.`
          );
        } else if (distance !== null && deliveryRadiusKm > 0) {
          setCustomerLocationMessage(
            `Location detect ho gayi. Distance ${distance.toFixed(
              2
            )} KM hai. Delivery available hai.`
          );
        } else if (!hasShopCoordinates) {
          setCustomerLocationMessage(
            'Customer location detect ho gayi, lekin shop location missing hai. Vendor location check karein.'
          );
        } else {
          setCustomerLocationMessage(
            `Location detect ho gayi. Accuracy approx ${Math.round(
              accuracy || 0
            )} meter.`
          );
        }

        const address = await reverseGeocodeCustomerLocation(lat, lng);

        if (address) {
          if (!addressLine1 && address.addressLine1) {
            setAddressLine1(address.addressLine1);
          }

          if (!addressLine2 && address.addressLine2) {
            setAddressLine2(address.addressLine2);
          }

          if (!city && address.city) {
            setCity(address.city);
          }

          if (!stateName && address.state) {
            setStateName(address.state);
          }

          if (!pincode && address.pincode) {
            setPincode(address.pincode);
          }

          if (address.displayName && !addressLine1) {
            setAddressLine1(address.displayName);
          }
        }

        setCustomerLocationLoading(false);
      },
      (error) => {
        console.error('Customer location error:', error);

        let msg = 'Location allow nahi hua. Please browser permission allow karein.';

        if (error.code === 1) {
          msg = 'Location permission denied hai. Browser me location allow karein.';
        } else if (error.code === 2) {
          msg = 'Location available nahi hai. GPS/Internet check karein.';
        } else if (error.code === 3) {
          msg = 'Location detect karne me timeout ho gaya. Dobara try karein.';
        }

        setCustomerLocationMessage(msg);
        setCustomerLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  const fetchPricingPreview = async () => {
    if (!vendorId || !cart.length) return;

    const contactItems = cart.filter((item) => Number(item.quantity || 0) > 0 && isContactPriceItem(item));
    const fixedItems = cart.filter((item) => Number(item.quantity || 0) > 0 && !isContactPriceItem(item));

    if (contactItems.length > 0) {
      const baseTotal = fixedItems.reduce((sum, item) => {
        return sum + Number(item.selling_price || 0) * Number(item.quantity || 0);
      }, 0);

      const deliveryCharge = orderMode === 'delivery' ? Number(orderSettings.delivery_fee || 0) : 0;
      const packagingFee = Number(orderSettings.packaging_fee || 0);
      const extraCharges = deliveryCharge + packagingFee;

      setPricingPreview({
        products: cart.map((item) => ({
          ...item,
          line_total: isContactPriceItem(item)
            ? 0
            : Number(item.selling_price || 0) * Number(item.quantity || 0),
          needs_price_confirmation: isContactPriceItem(item),
          price_label: isContactPriceItem(item) ? 'Price to be confirmed' : null,
        })),
        baseTotal,
        extraCharges,
        discountAmount: 0,
        finalTotal: baseTotal + extraCharges,
        validatedOfferName: null,
        appliedOffer: null,
        deliveryEtaMinutes: orderMode === 'delivery' ? Number(orderSettings.delivery_eta_max || 0) : null,
        hasContactPriceItems: true,
      });

      return;
    }

    try {
      setPreviewLoading(true);

      const response = await axios.post(`${apiUrl}/public/grocery/order/preview`, {
        vendorId: Number(vendorId),
        vendorType: 'grocery',
        businessType: 'grocery',
        groceryMode,
        mode: groceryMode,
        table: table || null,
        tableNumber: table || null,
        table_number: table || null,
        qrName: qrName || null,
        qr_name: qrName || null,
        order_mode: orderMode,
        order_type: 'individual',
        products: fixedItems.map((item) => ({
          productId: Number(item.productId),
          quantity: Number(item.quantity),
        })),
      });

      const preview = response.data?.preview || {};

      setPricingPreview({
        products: safeArray(preview.products),
        baseTotal: Number(preview.baseTotal || 0),
        extraCharges: Number(preview.extraCharges || 0),
        discountAmount: Number(preview.discountAmount || 0),
        finalTotal: Number(preview.finalTotal || 0),
        validatedOfferName: preview.validatedOfferName || null,
        appliedOffer: preview.appliedOffer || null,
        deliveryEtaMinutes: preview.deliveryEtaMinutes || null,
        hasContactPriceItems: false,
      });
    } catch (err) {
      const msg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Pricing preview failed.';

      setErrorMessage(msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const fetchTodayOrders = async () => {
    const mobile = normalizeMobile(trackMobile);

    if (!mobile || !/^\d{10}$/.test(mobile)) {
      setErrorMessage('Track order ke liye valid 10-digit mobile number required hai.');
      return;
    }

    if (!vendorId) {
      setErrorMessage('Invalid vendor.');
      return;
    }

    try {
      setTrackLoading(true);
      setTodayOrders([]);
      clearMessages();

      const response = await axios.get(`${apiUrl}/public/grocery/order-track-today`, {
        params: {
          vendorId,
          groceryMode,
          mobile,
        },
      });

      const orders = safeArray(response.data?.orders);
      setTodayOrders(orders);

      if (!orders.length) {
        setMessage('Aaj is mobile number se koi order nahi mila.');
      }
    } catch (err) {
      const msg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Order tracking failed.';

      setErrorMessage(msg);
    } finally {
      setTrackLoading(false);
    }
  };

  const categories = useMemo(() => {
    return [...new Set(allProducts.map((p) => (p.category || 'Other').trim()).filter(Boolean))];
  }, [allProducts]);

  const brands = useMemo(() => {
    return [...new Set(allProducts.map((p) => (p.brand || '').trim()).filter(Boolean))];
  }, [allProducts]);

  const filteredProducts = useMemo(() => {
    return allProducts.filter((product) => {
      const activeOk = Number(product.is_active ?? 1) === 1;

      const stockOk =
        Number(product.stock_quantity || 0) > 0 ||
        product.pricing_type === 'inquiry' ||
        product.pricing_type === 'hidden';

      const text = `${product.name || ''} ${product.category || ''} ${product.brand || ''} ${product.pack_size || ''}`.toLowerCase();

      const searchOk = !search.trim() || text.includes(search.trim().toLowerCase());
      const brandOk = !brandFilter || product.brand === brandFilter;
      const categoryOk = !categoryFilter || (product.category || 'Other') === categoryFilter;
      const trendingOk = !showTrendingOnly || Number(product.trend_item) === 1;

      return activeOk && stockOk && searchOk && brandOk && categoryOk && trendingOk;
    });
  }, [allProducts, search, brandFilter, categoryFilter, showTrendingOnly]);

  const groupedProducts = useMemo(() => {
    const map = {};

    filteredProducts.forEach((product) => {
      const key = (product.category || 'Other').trim() || 'Other';

      if (!map[key]) map[key] = [];
      map[key].push(product);
    });

    return map;
  }, [filteredProducts]);

  const activeCartItems = useMemo(
    () => cart.filter((item) => Number(item.quantity || 0) > 0),
    [cart]
  );

  const cartCount = useMemo(() => {
    return cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }, [cart]);

  const cartEstimateTotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      if (isContactPriceItem(item)) return sum;

      return sum + Number(item.selling_price || 0) * Number(item.quantity || 0);
    }, 0);
  }, [cart]);

  const renderPublicPrice = (product) => {
    if (product.pricing_type === 'inquiry') return 'Price on Request';
    if (product.pricing_type === 'hidden' || !Number(product.show_price)) return 'Contact for Price';
    if (product.selling_price == null || product.selling_price === '') return 'Price Not Set';

    return formatMoney(product.selling_price);
  };

  const getCartItem = (productId) => {
    return cart.find((item) => Number(item.productId) === Number(productId)) || null;
  };

  const findProductById = (productId) => {
    return allProducts.find((item) => Number(item.id) === Number(productId)) || null;
  };

  const upsertCartItem = (product, nextQty) => {
    setCart((prev) => {
      const existing = prev.find((item) => Number(item.productId) === Number(product.id));
      const finalQty = Math.max(0, Number(nextQty || 0));

      if (finalQty === 0) {
        return prev.filter((item) => Number(item.productId) !== Number(product.id));
      }

      const needsPriceConfirmation = isContactPriceItem(product);

      const payload = {
        productId: Number(product.id),
        name: product.name,
        image: product.image_url || product.image || '',
        category: product.category || '',
        brand: product.brand || '',
        pack_size: product.pack_size || '',
        unit_type: product.unit_type || '',
        pricing_type: product.pricing_type || 'fixed',
        show_price: Number(product.show_price ?? 1),
        selling_price: needsPriceConfirmation ? 0 : Number(product.selling_price || 0),
        stock_quantity: Number(product.stock_quantity || 0),
        moq: Math.max(1, Number(product.moq || 1)),
        quantity: finalQty,
        needs_price_confirmation: needsPriceConfirmation,
        price_label: needsPriceConfirmation ? 'Price to be confirmed' : null,
      };

      if (existing) {
        return prev.map((item) =>
          Number(item.productId) === Number(product.id) ? payload : item
        );
      }

      return [...prev, payload];
    });
  };

  const addProductToCart = (product) => {
    const existing = getCartItem(product.id);
    const currentQty = Number(existing?.quantity || 0);

    const minStep =
      groceryMode === 'wholesale'
        ? Math.max(1, Number(product.moq || 1))
        : 1;

    const proposedQty =
      currentQty > 0
        ? currentQty + minStep
        : Math.max(minStep, Number(product.moq || 1));

    const stockQty = Number(product.stock_quantity || 0);

    if (stockQty > 0 && proposedQty > stockQty) {
      setMessage(`"${product.name}" ke liye available stock sirf ${stockQty} hai.`);
      return;
    }

    upsertCartItem(product, proposedQty);

    if (isContactPriceItem(product)) {
      setMessage(`"${product.name}" order me add ho gaya. Iska final price vendor confirm karega.`);
    }
  };

  const increaseQty = (product) => {
    const realProduct = product.name ? product : findProductById(product.id || product.productId);
    if (!realProduct) return;

    const existing = getCartItem(realProduct.id);
    if (!existing) return addProductToCart(realProduct);

    const stepValue =
      groceryMode === 'wholesale'
        ? Math.max(1, Number(existing.moq || realProduct.moq || 1))
        : 1;

    const nextQty = Number(existing.quantity || 0) + stepValue;
    const stockQty = Number(existing.stock_quantity || realProduct.stock_quantity || 0);

    if (stockQty > 0 && nextQty > stockQty) {
      setMessage(`"${realProduct.name}" ke liye available stock sirf ${stockQty} hai.`);
      return;
    }

    upsertCartItem(realProduct, nextQty);
  };

  const decreaseQty = (product) => {
    const realProduct = product.name ? product : findProductById(product.id || product.productId);
    if (!realProduct) return;

    const existing = getCartItem(realProduct.id);
    if (!existing) return;

    const stepValue =
      groceryMode === 'wholesale'
        ? Math.max(1, Number(existing.moq || realProduct.moq || 1))
        : 1;

    const nextQty = Number(existing.quantity || 0) - stepValue;

    if (nextQty <= 0) {
      upsertCartItem(realProduct, 0);
      return;
    }

    upsertCartItem(realProduct, nextQty);
  };

  const removeFromCart = (productId) => {
    const realProduct = findProductById(productId);

    if (!realProduct) {
      setCart((prev) => prev.filter((item) => Number(item.productId) !== Number(productId)));
      return;
    }

    upsertCartItem(realProduct, 0);
  };

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');

      if (existingScript) {
        existingScript.onload = () => resolve(true);
        existingScript.onerror = () => resolve(false);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);

      document.body.appendChild(script);
    });

  const createPaymentOrder = async ({ products, orderMode, orderType, customerNumber }) => {
    const safeCustomer = customerNumber || DEFAULT_MOBILE_NUMBER;
    const idempotencyKey = `pay_${vendorId}_${groceryMode}_${safeCustomer}_${Date.now()}`;

    const response = await axios.post(`${apiUrl}/public/payment/create-order`, {
      vendorId: parseInt(vendorId, 10),
      vendorType: businessType,
      businessType,
      groceryMode,
      mode: groceryMode,
      table: table || null,
      tableNumber: table || null,
      table_number: table || null,
      qrName: qrName || null,
      qr_name: qrName || null,
      products,
      orderMode,
      orderType,
      customerNumber: safeCustomer,
      receipt: idempotencyKey,
      idempotencyKey,
    });

    return response.data;
  };

  const verifyOnlinePayment = async ({
    paymentId,
    razorpay_order_id,
    razorpay_payment_id,
    razorpay_signature,
  }) => {
    const response = await axios.post(`${apiUrl}/public/payment/verify`, {
      vendorId: parseInt(vendorId, 10),
      vendorType: businessType,
      paymentId,
      razorpay_order_id,
      razorpay_payment_id,
      razorpay_signature,
    });

    return response.data;
  };

  const openRazorpayCheckout = async ({ paymentData, orderTotal }) => {
    const sdkLoaded = await loadRazorpayScript();

    if (!sdkLoaded) {
      throw new Error('Razorpay SDK load nahi hua. Please internet check karein.');
    }

    return new Promise((resolve, reject) => {
      let successHandled = false;

      const options = {
        key: paymentData.key,
        amount: paymentData.amount,
        currency: paymentData.currency || 'INR',
        name: shopName || 'Grocery Store',
        description: 'Grocery Order Payment',
        order_id: paymentData.razorpay_order_id,
        prefill: {
          name: customerName || '',
          contact: mobileNumber || DEFAULT_MOBILE_NUMBER,
        },
        notes: {
          vendorId: String(vendorId),
          vendorType: businessType,
          groceryMode,
          orderMode,
        },
        theme: {
          color: '#0f766e',
        },
        modal: {
          confirm_close: true,
          escape: false,
          backdropclose: false,
          ondismiss: () => {
            if (successHandled) return;

            setIsPaymentVerifying(true);
            setPaymentStatusMessage('Payment verify ho raha hai, please wait.');
            setMessage('Payment verify ho raha hai, please wait.');

            clearPaymentDismissTimer();

            paymentDismissTimerRef.current = setTimeout(() => {
              if (successHandled) return;

              setIsPaymentVerifying(false);
              setPaymentStatusMessage('');
              setMessage('Payment complete nahi hua ya confirmation late aaya. Aap dobara try kar sakte hain.');
              reject(new Error('Payment popup band hua, lekin success confirmation nahi mila.'));
            }, 15000);
          },
        },
        handler: async function (response) {
          try {
            successHandled = true;
            clearPaymentDismissTimer();

            setIsPaymentVerifying(true);
            setPaymentStatusMessage('Payment verify ho raha hai, please wait.');
            setMessage('Payment verify ho raha hai, please wait.');

            await verifyOnlinePayment({
              paymentId: paymentData.paymentId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            setIsPaymentVerifying(false);
            setPaymentStatusMessage('');
            setMessage('');

            resolve({
              success: true,
              paymentStatus: 'paid',
              paymentMethod: 'razorpay',
              paymentReferenceId: paymentData.paymentId,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              total: orderTotal,
            });
          } catch (err) {
            clearPaymentDismissTimer();
            setIsPaymentVerifying(false);
            setPaymentStatusMessage('');
            reject(new Error(err?.response?.data?.error || err.message || 'Payment verify failed'));
          }
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on('payment.failed', function (response) {
        clearPaymentDismissTimer();
        setIsPaymentVerifying(false);
        setPaymentStatusMessage('');

        reject(
          new Error(
            response?.error?.description ||
              response?.error?.reason ||
              'Payment failed'
          )
        );
      });

      rzp.open();
    });
  };

  const validateModeStep = () => {
    clearMessages();

    const pickupEnabled = safeFlag(orderSettings.enable_pickup, 1) === 1;
    const deliveryEnabled = safeFlag(orderSettings.enable_home_delivery, 0) === 1;
    const shopStatus = getShopOpenStatus(orderSettings);

    if (!shopStatus.isOpen) {
      setErrorMessage(shopStatus.message);
      return false;
    }

    if (orderMode === 'pickup' && !pickupEnabled) {
      setErrorMessage('Takeaway/Pickup available nahi hai.');
      return false;
    }

    if (orderMode === 'delivery' && !deliveryEnabled) {
      setErrorMessage('Delivery available nahi hai.');
      return false;
    }

    return true;
  };

  const validateCustomerStep = () => {
    clearMessages();

    const enteredMobile = normalizeMobile(mobileNumber);

    if (!enteredMobile || !/^\d{10}$/.test(enteredMobile)) {
      setErrorMessage('Please enter valid 10-digit mobile number.');
      return false;
    }

    if (!customerName.trim()) {
      setErrorMessage('Please enter customer name.');
      return false;
    }

    if (orderMode === 'delivery') {
      if (!addressLine1.trim()) {
        setErrorMessage('Delivery ke liye address line 1 required hai.');
        return false;
      }

      if (!city.trim()) {
        setErrorMessage('Delivery ke liye city required hai.');
        return false;
      }

      if (!stateName.trim()) {
        setErrorMessage('Delivery ke liye state required hai.');
        return false;
      }

      if (!pincode.trim() || !/^\d{6}$/.test(pincode.trim())) {
        setErrorMessage('Delivery ke liye valid 6-digit pincode required hai.');
        return false;
      }

      if (deliveryRadiusKm > 0) {
        if (!hasShopCoordinates) {
          setErrorMessage('Shop location set nahi hai. Delivery order place nahi ho sakta.');
          return false;
        }

        if (!hasCustomerCoordinates) {
          setErrorMessage('Delivery ke liye customer location allow karna required hai.');
          return false;
        }

        if (!deliveryRangeStatus.allowed) {
          setErrorMessage(deliveryRangeStatus.message);
          return false;
        }
      }
    }

    setMobileNumber(enteredMobile);
    return true;
  };

  const validateProductsStep = () => {
    clearMessages();

    const shopStatus = getShopOpenStatus(orderSettings);

    if (!shopStatus.isOpen) {
      setErrorMessage(shopStatus.message);
      return false;
    }

    if (!cart.length || activeCartItems.length === 0) {
      setErrorMessage('Please select at least one product.');
      return false;
    }

    if (previewLoading) {
      setErrorMessage('Pricing preview abhi load ho raha hai.');
      return false;
    }

    if (!hasContactPriceItems && !pricingPreview.finalTotal && activeCartItems.length > 0) {
      setErrorMessage('Server pricing preview generate nahi hua.');
      return false;
    }

    if (
      !hasContactPriceItems &&
      Number(orderSettings.min_order_amount || 0) > 0 &&
      Number(pricingPreview.finalTotal || 0) < Number(orderSettings.min_order_amount || 0)
    ) {
      setErrorMessage(`Minimum order ${formatMoney(orderSettings.min_order_amount)} hona chahiye.`);
      return false;
    }

    return true;
  };

  const validatePaymentStep = () => {
    clearMessages();

    const shopStatus = getShopOpenStatus(orderSettings);
    const codAllowed = safeFlag(orderSettings.allow_cod, 1) === 1;
    const onlineAllowed = safeFlag(orderSettings.allow_online_payment, 1) === 1;

    if (!shopStatus.isOpen) {
      setErrorMessage(shopStatus.message);
      return false;
    }

    if (hasContactPriceItems) {
      if (paymentMode === 'online') {
        setPaymentMode('cash');
      }

      return true;
    }

    if (!codAllowed && onlineAllowed && paymentMode !== 'online') {
      setPaymentMode('online');
      return true;
    }

    if (paymentMode === 'cash' && !codAllowed) {
      setErrorMessage('Cash/COD option allowed nahi hai. Online payment se order place karein.');
      return false;
    }

    if (paymentMode === 'online' && !onlineAllowed) {
      setErrorMessage('Online payment allowed nahi hai.');
      return false;
    }

    if (!codAllowed && !onlineAllowed) {
      setErrorMessage('Is shop me abhi payment option available nahi hai. Shop owner se contact karein.');
      return false;
    }

    return true;
  };

  const buildPayload = () => {
    const safeCustomerNumber = normalizeMobile(mobileNumber) || DEFAULT_MOBILE_NUMBER;

    return {
      customer_number: safeCustomerNumber,
      customer_name: customerName?.trim() || null,

      vendorId: Number(vendorId),
      vendorType: 'grocery',
      businessType: 'grocery',
      groceryMode,
      mode: groceryMode,
      table: table || null,
      tableNumber: table || null,
      table_number: table || null,
      qrName: qrName || null,
      qr_name: qrName || null,

      products: activeCartItems.map((item) => ({
        productId: Number(item.productId),
        quantity: Number(item.quantity),
        pricing_type: item.pricing_type || 'fixed',
        needs_price_confirmation: isContactPriceItem(item),
        price_label: isContactPriceItem(item) ? 'Price to be confirmed' : null,
        customer_visible_price: isContactPriceItem(item) ? null : Number(item.selling_price || 0),
      })),

      order_type: 'individual',
      offer_name: pricingPreview.validatedOfferName || null,
      table_number: table || null,
      order_mode: orderMode,
      online_source: 'qr',

      paymentReferenceId:
        paymentMode === 'online' && !hasContactPriceItems
          ? String(verifiedPaymentData?.paymentId || '')
          : null,

      idempotencyKey: `${vendorId}-${groceryMode}-${safeCustomerNumber}-${Date.now()}`,

      customer_details: {
        customer_name: customerName?.trim() || null,
        customer_mobile: safeCustomerNumber,
        customer_type: groceryMode,
        address_line1: addressLine1 || null,
        address_line2: addressLine2 || null,
        landmark: landmark || null,
        city: city || null,
        state: stateName || null,
        pincode: pincode || null,
        notes: deliveryNotes || null,
      },

      delivery_details: {
        customer_name: customerName?.trim() || null,
        customer_mobile: safeCustomerNumber,
        address_line1: orderMode === 'delivery' ? addressLine1 || null : null,
        address_line2: orderMode === 'delivery' ? addressLine2 || null : null,
        landmark: orderMode === 'delivery' ? landmark || null : null,
        city: orderMode === 'delivery' ? city || null : null,
        state: orderMode === 'delivery' ? stateName || null : null,
        pincode: orderMode === 'delivery' ? pincode || null : null,
        latitude:
          orderMode === 'delivery' && hasCustomerCoordinates
            ? Number(customerLatitude)
            : null,
        longitude:
          orderMode === 'delivery' && hasCustomerCoordinates
            ? Number(customerLongitude)
            : null,
        customer_latitude:
          orderMode === 'delivery' && hasCustomerCoordinates
            ? Number(customerLatitude)
            : null,
        customer_longitude:
          orderMode === 'delivery' && hasCustomerCoordinates
            ? Number(customerLongitude)
            : null,
        delivery_distance_km:
          orderMode === 'delivery' &&
          deliveryRangeStatus.distanceKm !== null &&
          deliveryRangeStatus.distanceKm !== undefined
            ? Number(Number(deliveryRangeStatus.distanceKm).toFixed(2))
            : orderMode === 'delivery' &&
                deliveryDistanceKm !== null &&
                deliveryDistanceKm !== undefined
              ? Number(Number(deliveryDistanceKm).toFixed(2))
              : null,
        location_accuracy_meters:
          orderMode === 'delivery' && customerLocationAccuracy
            ? Math.round(Number(customerLocationAccuracy))
            : null,
        delivery_notes: orderMode === 'delivery' ? deliveryNotes || null : null,
      },
    };
  };

  const handlePlaceOrder = async () => {
    const latestQrStatus = await checkQrActiveStatus();

    if (!latestQrStatus.isActive) {
      setQrAccess({
        checked: true,
        isActive: false,
        message:
          latestQrStatus.message ||
          'This QR code is not active. Please contact the shop.',
      });

      setErrorMessage(
        latestQrStatus.message ||
          'This QR code is not active. Please contact the shop.'
      );
      return;
    }

    const shopStatus = getShopOpenStatus(orderSettings);

    if (!shopStatus.isOpen) {
      setStep(STEP_MODE);
      setErrorMessage(shopStatus.message);
      return;
    }

    if (!validateCustomerStep()) {
      setStep(STEP_CUSTOMER);
      return;
    }

    if (!validateProductsStep()) {
      setStep(STEP_REVIEW);
      return;
    }

    if (!validatePaymentStep()) {
      setStep(STEP_PAYMENT);
      return;
    }

    setPlacingOrder(true);
    clearMessages();

    try {
      let paymentSnapshot = verifiedPaymentData;
      const shouldUseOnlinePayment = paymentMode === 'online' && !hasContactPriceItems;

      if (hasContactPriceItems) {
        setPaymentMode('cash');
      }

      if (shouldUseOnlinePayment) {
        if (!paymentSnapshot || !paidOnline) {
          setPaymentLoading(true);
          setMessage('Payment window open ho rahi hai.');

          const paymentData = await createPaymentOrder({
            products: activeCartItems.map((item) => ({
              productId: Number(item.productId),
              quantity: Number(item.quantity),
            })),
            orderMode,
            orderType: 'individual',
            customerNumber: normalizeMobile(mobileNumber) || DEFAULT_MOBILE_NUMBER,
          });

          await openRazorpayCheckout({
            paymentData,
            orderTotal: Number(paymentData.calculatedAmount || pricingPreview.finalTotal || 0),
          });

          paymentSnapshot = {
            paymentId: paymentData.paymentId,
            razorpay_order_id: paymentData.razorpay_order_id,
            amount: paymentData.amount,
            calculatedAmount: paymentData.calculatedAmount,
          };

          setVerifiedPaymentData(paymentSnapshot);
          setPaidOnline(true);
          setMessage('Payment successful. Ab order place ho raha hai.');
        }
      }

      const payload = buildPayload();

      if (shouldUseOnlinePayment && paymentSnapshot?.paymentId) {
        payload.paymentReferenceId = String(paymentSnapshot.paymentId);
      }

      const response = await axios.post(`${apiUrl}/public/grocery/order/place`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      const orderId = response.data?.orderId;
      const totalPrice = Number(response.data?.totalPrice || pricingPreview.finalTotal || 0).toFixed(2);
      const backendRedeem = response.data?.redeem || null;

      const fallbackExpiry = new Date();
      fallbackExpiry.setDate(fallbackExpiry.getDate() + 7);

      const successOrder = {
        order_id: orderId || '-',
        id: orderId || '-',
        total_price: totalPrice,
        totalPrice,
        final_total: totalPrice,
        finalTotal: totalPrice,

        order_mode: orderMode,
        orderMode,
        grocery_mode: groceryMode,

        customer_number: normalizeMobile(mobileNumber),
        customer_name: customerName,

        status: 'pending',
        logs: [
          {
            id: 'new-order-pending',
            new_status: 'pending',
            status: 'pending',
            note: 'Order placed from QR',
            created_at: 'Just now',
          },
        ],

        payment_status: shouldUseOnlinePayment ? 'paid' : hasContactPriceItems ? 'unpaid' : orderMode === 'delivery' ? 'cod' : 'unpaid',
        paymentStatus: shouldUseOnlinePayment ? 'paid' : hasContactPriceItems ? 'unpaid' : orderMode === 'delivery' ? 'cod' : 'unpaid',
        payment_method: shouldUseOnlinePayment ? 'razorpay' : hasContactPriceItems ? 'none' : orderMode === 'delivery' ? 'cash' : 'none',
        paymentMethod: shouldUseOnlinePayment ? 'razorpay' : hasContactPriceItems ? 'none' : orderMode === 'delivery' ? 'cash' : 'none',

        payment_reference_id: shouldUseOnlinePayment ? String(paymentSnapshot?.paymentId || '') : null,
        razorpay_order_id: shouldUseOnlinePayment ? paymentSnapshot?.razorpay_order_id || null : null,

        token_number: backendRedeem?.token || '-',
        tokenNumber: backendRedeem?.token || '-',
        redeem_code: backendRedeem?.code || '-',
        redeemCode: backendRedeem?.code || '-',
        expires_on: backendRedeem?.expiry_date
          ? new Date(backendRedeem.expiry_date).toLocaleDateString()
          : fallbackExpiry.toLocaleDateString(),
        expireDate: backendRedeem?.expiry_date
          ? new Date(backendRedeem.expiry_date).toLocaleDateString()
          : fallbackExpiry.toLocaleDateString(),

        products: pricingPreview.products?.length ? pricingPreview.products : activeCartItems,
        selectedProducts: pricingPreview.products?.length ? pricingPreview.products : activeCartItems,
        redeemSaved: !!backendRedeem,
      };

      setPlacedOrder(successOrder);
      setStep(STEP_SUCCESS);

      setCart([]);
      setPaidOnline(false);
      setVerifiedPaymentData(null);
      setPaymentLoading(false);
      setIsPaymentVerifying(false);
      setPaymentStatusMessage('');
      setShowClaimInput(false);
      setManualRedeemCode('');
      setContactProduct(null);
    } catch (err) {
      const msg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Order place failed';

      setErrorMessage(msg);
    } finally {
      setPlacingOrder(false);
      setPaymentLoading(false);
      setIsPaymentVerifying(false);
      setPaymentStatusMessage('');
    }
  };

  const resetWholeFlow = () => {
    setStep(STEP_LANDING);
    clearMessages();

    setSearch('');
    setBrandFilter('');
    setCategoryFilter('');
    setShowTrendingOnly(false);

    setCart([]);
    setOrderMode(safeFlag(orderSettings.enable_pickup, 1) === 1 ? 'pickup' : 'delivery');
    setPaymentMode(
      safeFlag(orderSettings.allow_cod, 1) === 1
        ? 'cash'
        : safeFlag(orderSettings.allow_online_payment, 1) === 1
          ? 'online'
          : 'cash'
    );

    setMobileNumber('');
    setCustomerName('');
    setAddressLine1('');
    setAddressLine2('');
    setLandmark('');
    setCity('');
    setStateName('');
    setPincode('');
    setDeliveryNotes('');

    setCustomerFound(false);
    setCustomerLookupMessage('');
    setCustomerLookupLoading(false);

    setTrackMobile('');
    setTrackLoading(false);
    setTodayOrders([]);

    setPlacedOrder(null);
    setShowClaimInput(false);
    setManualRedeemCode('');

    setPaidOnline(false);
    setVerifiedPaymentData(null);
    setPaymentLoading(false);
    setIsPaymentVerifying(false);
    setPaymentStatusMessage('');
    setContactProduct(null);

    clearPaymentDismissTimer();

    setPricingPreview({
      products: [],
      baseTotal: 0,
      extraCharges: 0,
      discountAmount: 0,
      finalTotal: 0,
      validatedOfferName: null,
      appliedOffer: null,
      deliveryEtaMinutes: null,
    });
  };

  const handleClaimNow = () => {
    setShowClaimInput(true);
  };

  const handleClaimRedeem = () => {
    if (!manualRedeemCode.trim()) {
      setMessage('Please enter redeem code.');
      return;
    }

    setMessage(`Redeem code "${manualRedeemCode}" claim flow ke liye ready hai.`);
  };

  const getOrderProducts = (order) => {
    const direct =
      order?.selectedProducts ||
      order?.products ||
      order?.productsParsed ||
      order?.items ||
      order?.orderedProducts;

    return safeParseProducts(direct);
  };

  const getOrderId = (order) => order?.order_id || order?.id || order?.orderId || '-';

  const getOrderTotal = (order) =>
    order?.final_total ||
    order?.finalTotal ||
    order?.total_price ||
    order?.totalPrice ||
    order?.amount ||
    0;

  const getOrderCustomerName = (order) =>
    order?.customer_name ||
    order?.customerName ||
    order?.name ||
    customerName ||
    '-';

  const getOrderPaymentStatus = (order) =>
    order?.payment_status ||
    order?.paymentStatus ||
    'unpaid';

  const getOrderPaymentMethod = (order) =>
    order?.payment_method ||
    order?.paymentMethod ||
    'none';

  const getOrderToken = (order) =>
    order?.token_number ||
    order?.tokenNumber ||
    order?.redeem?.token ||
    '-';

  const getOrderRedeemCode = (order) =>
    order?.redeem_code ||
    order?.redeemCode ||
    order?.redeem?.code ||
    '-';

  const getOrderExpiry = (order) =>
    order?.expires_on ||
    order?.expireDate ||
    order?.expiry_date ||
    order?.redeem?.expiry_date ||
    '-';

  const getOrderMode = (order) =>
    order?.order_mode ||
    order?.orderMode ||
    '-';

  const getProductLineTotal = (item) => {
    if (isContactPriceItem(item)) return 0;

    return (
      item.line_total ||
      item.total ||
      item.total_price ||
      Number(item.selling_price || item.price || item.unit_price || 0) *
        Number(item.quantity || item.qty || 0)
    );
  };

  const getProductPriceLabel = (item) => {
    if (isContactPriceItem(item)) return 'Price to be confirmed';
    return formatMoney(getProductLineTotal(item));
  };

  const getProductQty = (item) => Number(item.quantity || item.qty || 0);

  const renderProductAction = (product) => {
    const cartItem = getCartItem(product.id);

    const isFixedVisible =
      product.pricing_type === 'fixed' &&
      Number(product.show_price) === 1 &&
      Number(product.stock_quantity || 0) > 0;

    if (isFixedVisible || isContactPriceItem(product)) {
      if (!cartItem) {
        return (
          <div className="grocery-contact-action-stack">
            {isContactPriceItem(product) ? (
              <button
                type="button"
                className="grocery-add-btn quote"
                onClick={() => {
                  clearMessages();
                  setContactProduct(product);
                }}
              >
                Contact
              </button>
            ) : null}

            <button
              type="button"
              className={`grocery-add-btn ${isContactPriceItem(product) ? 'confirm' : ''}`}
              onClick={() => addProductToCart(product)}
            >
              {isContactPriceItem(product) ? 'Add Order' : 'Add'}
            </button>
          </div>
        );
      }

      return (
        <div className="grocery-qty-controls">
          <button type="button" className="grocery-qty-btn" onClick={() => decreaseQty(product)}>
            -
          </button>

          <span className="grocery-qty-display">{cartItem.quantity}</span>

          <button type="button" className="grocery-qty-btn active" onClick={() => increaseQty(product)}>
            +
          </button>
        </div>
      );
    }

    return (
      <button
        type="button"
        className="grocery-add-btn quote"
        onClick={() => {
          clearMessages();
          setContactProduct(product);
        }}
      >
        Contact
      </button>
    );
  };

  const renderCategorySliderCard = (product) => {
    const imageSrc = product.image_url || product.image || PLACEHOLDER_IMAGE;
    const cartItem = getCartItem(product.id);

    return (
      <div key={product.id} className={`grocery-slider-card ${cartItem ? 'selected' : ''}`}>
        <div className="grocery-slider-image-wrap">
          <img src={imageSrc} alt={product.name} />
          {Number(product.trend_item) === 1 ? <span className="grocery-mini-badge">Trending</span> : null}
          {cartItem ? <span className="grocery-check-badge">✓</span> : null}
        </div>

        <div className="grocery-slider-body">
          <div className="grocery-slider-name">{product.name}</div>

          <div className="grocery-slider-pack">
            {product.pack_size || '-'} {product.unit_type ? `| ${product.unit_type}` : ''}
          </div>

          <div className="grocery-slider-price-row">
            <span className="grocery-slider-price">{renderPublicPrice(product)}</span>
          </div>

          {groceryMode === 'wholesale' ? (
            <div className="grocery-meta-line">
              Min Qty: {Math.max(1, Number(product.moq || 1))}
            </div>
          ) : null}

          <div className="grocery-meta-line">Stock: {product.stock_quantity || 0}</div>

          <div className="grocery-slider-action">
            {renderProductAction(product)}
          </div>

          {cartItem?.quantity > 0 ? (
            <div className="grocery-meta-line grocery-in-cart-line">
              In Cart: {cartItem.quantity}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderSelectedPreview = () => {
    if (!activeCartItems.length) return null;

    return (
      <div className="grocery-selected-preview">
        <div className="grocery-section-head">
          <h3 className="grocery-section-title">Selected Products</h3>
          <span className="grocery-view-all">{cartCount} item(s)</span>
        </div>

        <div className="grocery-selected-chip-row">
          {activeCartItems.slice(0, 10).map((item) => (
            <button
              key={item.productId}
              type="button"
              className="grocery-selected-chip"
              onClick={() => removeFromCart(item.productId)}
              title="Remove item"
            >
              {item.name} × {item.quantity}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderVendorContactPopup = () => {
    if (!contactProduct) return null;

    const cleanNumber = String(vendorNumber || '').replace(/\D/g, '');
    const callNumber = cleanNumber ? `tel:${cleanNumber}` : '';
    const whatsappNumber = cleanNumber.length === 10 ? `91${cleanNumber}` : cleanNumber;

    const productName = contactProduct.name || 'Product';
    const productPack = `${contactProduct.pack_size || ''} ${contactProduct.unit_type || ''}`.trim();

    const whatsappText = encodeURIComponent(
      `Namaste ${shopName}, mujhe "${productName}${productPack ? ` (${productPack})` : ''}" ka price puchna hai. Price confirm hone ke baad main isko order me add karna chahta/chahti hoon.`
    );

    return (
      <div className="grocery-contact-overlay" onClick={() => setContactProduct(null)}>
        <div className="grocery-contact-card" onClick={(event) => event.stopPropagation()}>
          <button
            type="button"
            className="grocery-contact-close"
            onClick={() => setContactProduct(null)}
          >
            ×
          </button>

          <div className="grocery-contact-head">
            <div className="grocery-contact-icon">📞</div>

            <div>
              <h3>Contact Shop</h3>
              <p>Is product ka price shop se confirm karein.</p>
            </div>
          </div>

          <div className="grocery-contact-product">
            <strong>{productName}</strong>
            <span>{productPack || contactProduct.category || 'Price on request'}</span>
          </div>

          <div className="grocery-contact-shop">
            <div>
              <span>Shop Name</span>
              <strong>{shopName}</strong>
            </div>

            <div>
              <span>Mobile Number</span>
              <strong>{vendorNumber || 'Number not available'}</strong>
            </div>

            {shopLocation ? (
              <div>
                <span>Location</span>
                <strong>{shopLocation}</strong>
              </div>
            ) : null}
          </div>

          {!vendorNumber ? (
            <div className="grocery-error grocery-compact-msg">
              Vendor ka mobile number available nahi hai.
            </div>
          ) : null}

          <div className="grocery-contact-actions grocery-contact-actions-four">
            <button
              type="button"
              className="grocery-action-button secondary small"
              onClick={() => setContactProduct(null)}
            >
              Close
            </button>

            {callNumber ? (
              <a className="grocery-action-button small" href={callNumber}>
                Call
              </a>
            ) : (
              <button className="grocery-action-button small" disabled>
                Call
              </button>
            )}

            {whatsappNumber ? (
              <a
                className="grocery-action-button small whatsapp"
                href={`https://wa.me/${whatsappNumber}?text=${whatsappText}`}
                target="_blank"
                rel="noreferrer"
              >
                WhatsApp
              </a>
            ) : (
              <button className="grocery-action-button small whatsapp" disabled>
                WhatsApp
              </button>
            )}

            <button
              type="button"
              className="grocery-action-button small"
              onClick={() => {
                addProductToCart(contactProduct);
                setContactProduct(null);
              }}
            >
              Add Order
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderHeader = () => (
    <>
      {shopName ? <h1 className="grocery-shop-name">{shopName}</h1> : null}

      <div className="grocery-qr-container grocery-message-shell">
        {message ? <div className="grocery-message">{message}</div> : null}
        {errorMessage ? <div className="grocery-error">{errorMessage}</div> : null}
        {paymentStatusMessage ? <div className="grocery-message">{paymentStatusMessage}</div> : null}
      </div>
    </>
  );

  const renderLandingStep = () => (
    <div className="grocery-qr-container">
      <div className="grocery-landing-card grocery-landing-v2">
        <div className="grocery-chip-row">
          <span className="grocery-chip">Grocery</span>
          <span className="grocery-chip">{groceryMode === 'wholesale' ? 'Wholesale' : 'Retail'}</span>
          {qrName ? <span className="grocery-chip">{qrName}</span> : null}
          {table ? <span className="grocery-chip">QR: {table}</span> : null}
        </div>

        <div className="grocery-landing-shop">
          <div className="grocery-landing-logo">
            {shopImage ? (
              <img src={shopImage} alt={shopName} />
            ) : (
              <span>🛒</span>
            )}
          </div>

          <div>
            <h2>Welcome!</h2>
            <p>{shopLocation || 'Choose an option to continue'}</p>
          </div>
        </div>

        <div className="grocery-landing-action-grid">
          <button
            className="grocery-big-action-btn booking"
            onClick={() => {
              clearMessages();
              setStep(STEP_MODE);
            }}
          >
            <span>🛍️</span>
            <div>
              <strong>Booking Now</strong>
              <small>New grocery order place karein</small>
            </div>
          </button>

          <button
            className="grocery-big-action-btn track"
            onClick={() => {
              clearMessages();
              setStep(STEP_TRACK);
              setTrackMobile(normalizeMobile(mobileNumber));
            }}
          >
            <span>🔍</span>
            <div>
              <strong>Track Your Order</strong>
              <small>Aaj ka order status dekhein</small>
            </div>
          </button>
        </div>

        <div className="grocery-landing-note">
          <strong>Simple Flow:</strong> Mode select karein, mobile number dalein, same customer ka address auto-fill ho jayega.
        </div>
      </div>
    </div>
  );

  const renderModeStep = () => {
    const pickupEnabled = safeFlag(orderSettings.enable_pickup, 1) === 1;
    const deliveryEnabled = safeFlag(orderSettings.enable_home_delivery, 0) === 1;
    const codAllowed = safeFlag(orderSettings.allow_cod, 1) === 1;
    const onlineAllowed = safeFlag(orderSettings.allow_online_payment, 1) === 1;
    const shopStatus = getShopOpenStatus(orderSettings);

    return (
      <div className="grocery-qr-container">
        <div className="grocery-card grocery-step-card">
          <div className="grocery-step-head">
            <div>
              <span className="grocery-step-badge">Step 1 / 5</span>
              <h3 className="grocery-section-title">Select Booking Mode</h3>
            </div>
            <button className="grocery-mini-link-btn" onClick={() => setStep(STEP_LANDING)}>
              Back
            </button>
          </div>

          <div className={`grocery-shop-status-box ${shopStatus.isOpen ? 'open' : 'closed'}`}>
            <span>Shop Status</span>
            <strong>{shopStatus.label}</strong>
            <p>{shopStatus.message}</p>
          </div>

          <div className="grocery-mode-grid">
            <button
              type="button"
              className={`grocery-mode-btn ${orderMode === 'pickup' ? 'active' : ''}`}
              onClick={() => pickupEnabled && shopStatus.isOpen && setOrderMode('pickup')}
              disabled={!pickupEnabled || !shopStatus.isOpen}
            >
              🛍️ Takeaway
            </button>

            <button
              type="button"
              className={`grocery-mode-btn ${orderMode === 'delivery' ? 'active' : ''}`}
              onClick={() => deliveryEnabled && shopStatus.isOpen && setOrderMode('delivery')}
              disabled={!deliveryEnabled || !shopStatus.isOpen}
            >
              🛵 Delivery
            </button>
          </div>

          <div className="grocery-mode-info-grid">
            <div>
              <span>Minimum Order</span>
              <strong>{formatMoney(orderSettings.min_order_amount)}</strong>
            </div>

            <div>
              <span>Packaging Fee</span>
              <strong>{formatMoney(orderSettings.packaging_fee)}</strong>
            </div>

            <div>
              <span>Shop Timing</span>
              <strong>{getShopTimingText(orderSettings)}</strong>
            </div>

            <div>
              <span>Payment</span>
              <strong>
                {codAllowed && onlineAllowed
                  ? 'Cash / Online'
                  : onlineAllowed
                    ? 'Online Only'
                    : codAllowed
                      ? 'Cash Only'
                      : 'Not Available'}
              </strong>
            </div>

            {orderMode === 'delivery' ? (
              <>
                <div>
                  <span>Delivery Fee</span>
                  <strong>{formatMoney(orderSettings.delivery_fee)}</strong>
                </div>

                <div>
                  <span>ETA</span>
                  <strong>{orderSettings.delivery_eta_min || 30}-{orderSettings.delivery_eta_max || 60} min</strong>
                </div>

                <div>
                  <span>Delivery Radius</span>
                  <strong>
                    {Number(orderSettings.delivery_radius_km || 0) > 0
                      ? `${orderSettings.delivery_radius_km} KM`
                      : 'Not set'}
                  </strong>
                </div>
              </>
            ) : null}
          </div>

          {!shopStatus.isOpen ? (
            <div className="grocery-error grocery-compact-msg">
              Shop closed hai. Abhi order place nahi ho sakta.
            </div>
          ) : null}

          {!codAllowed && onlineAllowed && shopStatus.isOpen ? (
            <div className="grocery-message grocery-compact-msg">
              Is shop me Cash/COD बंद hai. Order place karne ke liye Online Payment karna padega.
            </div>
          ) : null}

          {!codAllowed && !onlineAllowed ? (
            <div className="grocery-error grocery-compact-msg">
              Is shop me abhi Cash aur Online dono payment बंद hain. Shop owner se contact karein.
            </div>
          ) : null}

          <div className="grocery-actions grocery-inline-actions">
            <button className="grocery-action-button secondary small" onClick={() => setStep(STEP_LANDING)}>
              Back
            </button>

            <button
              className="grocery-action-button small"
              onClick={() => {
                if (!validateModeStep()) return;
                setStep(STEP_CUSTOMER);
              }}
              disabled={!shopStatus.isOpen || (!pickupEnabled && !deliveryEnabled)}
            >
              {shopStatus.isOpen ? 'Continue' : 'Shop Closed'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCustomerStep = () => (
    <div className="grocery-qr-container">
      <div className="grocery-card grocery-step-card">
        <div className="grocery-step-head">
          <div>
            <span className="grocery-step-badge">Step 2 / 5</span>
            <h3 className="grocery-section-title">Customer Details</h3>
          </div>
          <button className="grocery-mini-link-btn" onClick={() => setStep(STEP_MODE)}>
            Back
          </button>
        </div>

        <div className="grocery-customer-tip">
          <strong>Important:</strong> Pehle mobile number dalein. Same mobile mila to name aur delivery address auto-fill ho jayega.
        </div>

        <div className="grocery-two-col-grid">
          <div className="grocery-form-group">
            <label>Mobile Number *</label>
            <input
              type="text"
              placeholder="10 digit mobile"
              value={mobileNumber}
              onChange={(event) => setMobileNumber(normalizeMobile(event.target.value))}
              inputMode="numeric"
            />
          </div>

          <div className="grocery-form-group">
            <label>Customer Name *</label>
            <input
              type="text"
              placeholder="Customer name"
              value={customerName}
              onChange={(event) => setCustomerName(event.target.value)}
            />
          </div>
        </div>

        {customerLookupLoading ? (
          <div className="grocery-message grocery-compact-msg">
            Customer details check ho raha hai...
          </div>
        ) : null}

        {customerLookupMessage ? (
          <div className={`${customerFound ? 'grocery-message' : 'grocery-error'} grocery-compact-msg`}>
            {customerLookupMessage}
          </div>
        ) : null}

        {orderMode === 'delivery' ? (
          <div className="grocery-delivery-fields">
            <div className="delivery-location-panel">
              <div className="delivery-location-head">
                <div>
                  <strong>📍 Delivery Location</strong>
                  <small>
                    {deliveryRadiusKm > 0
                      ? `Shop delivery radius: ${deliveryRadiusKm} KM`
                      : 'Shop ne delivery radius set nahi kiya hai.'}
                  </small>
                </div>

                <button
                  type="button"
                  className="delivery-location-btn"
                  onClick={handleUseCustomerLocation}
                  disabled={customerLocationLoading}
                >
                  {customerLocationLoading ? 'Detecting...' : 'Use My Current Location'}
                </button>
              </div>

              {customerLocationMessage ? (
                <div
                  className={`delivery-location-message ${
                    deliveryRangeStatus.checked && deliveryRangeStatus.allowed
                      ? 'success'
                      : deliveryRangeStatus.checked && !deliveryRangeStatus.allowed
                        ? 'danger'
                        : ''
                  }`}
                >
                  {customerLocationMessage}
                </div>
              ) : null}

              {deliveryRangeStatus.message ? (
                <div
                  className={`delivery-range-status ${
                    deliveryRangeStatus.allowed ? 'success' : 'danger'
                  }`}
                >
                  {deliveryRangeStatus.message}
                </div>
              ) : null}

              {hasCustomerCoordinates ? (
                <div className="delivery-location-meta">
                  <span>Lat: {customerLatitude}</span>
                  <span>Lng: {customerLongitude}</span>
                  {customerLocationAccuracy ? (
                    <span>Accuracy: {Math.round(customerLocationAccuracy)}m</span>
                  ) : null}
                  {deliveryRangeStatus.distanceKm !== null ? (
                    <span>Distance: {deliveryRangeStatus.distanceKm.toFixed(2)} KM</span>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="grocery-two-col-grid">
              <div className="grocery-form-group">
                <label>Address Line 1 *</label>
                <input
                  type="text"
                  value={addressLine1}
                  onChange={(event) => setAddressLine1(event.target.value)}
                  placeholder="House / shop / area"
                />
              </div>

              <div className="grocery-form-group">
                <label>Address Line 2</label>
                <input
                  type="text"
                  value={addressLine2}
                  onChange={(event) => setAddressLine2(event.target.value)}
                  placeholder="Road / building"
                />
              </div>
            </div>

            <div className="grocery-two-col-grid">
              <div className="grocery-form-group">
                <label>Landmark</label>
                <input
                  type="text"
                  value={landmark}
                  onChange={(event) => setLandmark(event.target.value)}
                  placeholder="Near..."
                />
              </div>

              <div className="grocery-form-group">
                <label>City *</label>
                <input
                  type="text"
                  value={city}
                  onChange={(event) => setCity(event.target.value)}
                  placeholder="City"
                />
              </div>
            </div>

            <div className="grocery-two-col-grid">
              <div className="grocery-form-group">
                <label>State *</label>
                <input
                  type="text"
                  value={stateName}
                  onChange={(event) => setStateName(event.target.value)}
                  placeholder="State"
                />
              </div>

              <div className="grocery-form-group">
                <label>Pincode *</label>
                <input
                  type="text"
                  value={pincode}
                  onChange={(event) => setPincode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  placeholder="6 digit pincode"
                />
              </div>
            </div>

            <div className="grocery-form-group">
              <label>Delivery Notes</label>
              <textarea
                value={deliveryNotes}
                onChange={(event) => setDeliveryNotes(event.target.value)}
                placeholder="Gate code, call before delivery, etc."
              />
            </div>
          </div>
        ) : (
          <div className="grocery-customer-tip success">
            Takeaway selected hai. Address required nahi hai, lekin mobile/name save hoga.
          </div>
        )}

        <div className="grocery-actions grocery-inline-actions">
          <button className="grocery-action-button secondary small" onClick={() => setStep(STEP_MODE)}>
            Back
          </button>

          <button
            className="grocery-action-button small"
            onClick={() => {
              if (!validateCustomerStep()) return;
              setStep(STEP_PRODUCTS);
            }}
          >
            Continue Products
          </button>
        </div>
      </div>
    </div>
  );

  const renderProductsStep = () => (
    <div className="grocery-qr-container grocery-products-container">
      <div className="grocery-search-sticky-wrap">
        <div className="grocery-search-topbar">
          <button
            type="button"
            className="grocery-back-icon-btn"
            onClick={() => setStep(STEP_CUSTOMER)}
            aria-label="Back"
            title="Back"
          >
            ←
          </button>

          <div className="grocery-search-box">
            <input
              type="text"
              placeholder="Search groceries..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        <div className="grocery-filter-grid">
          <div className="grocery-form-group">
            <select value={brandFilter} onChange={(event) => setBrandFilter(event.target.value)}>
              <option value="">All Brands</option>
              {brands.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </div>

          <div className="grocery-form-group">
            <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
              <option value="">All Categories</option>
              {categories.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className={`grocery-mode-btn ${showTrendingOnly ? 'active' : ''}`}
            onClick={() => setShowTrendingOnly((prev) => !prev)}
          >
            {showTrendingOnly ? 'Trending Only' : 'Show All'}
          </button>

          <button
            type="button"
            className="grocery-mode-btn"
            onClick={() => {
              setSearch('');
              setBrandFilter('');
              setCategoryFilter('');
              setShowTrendingOnly(false);
            }}
          >
            Clear
          </button>
        </div>

        {renderSelectedPreview()}
      </div>

      {Object.keys(groupedProducts).length === 0 ? (
        <div className="grocery-card">
          <div className="grocery-empty-state">Product not found.</div>
        </div>
      ) : (
        Object.entries(groupedProducts).map(([category, products]) => (
          <div className="grocery-card product-section-card" key={category}>
            <div className="grocery-section-head">
              <h3 className="grocery-section-title">{category}</h3>
              <span className="grocery-view-all">{products.length} item(s)</span>
            </div>

            <div className="grocery-horizontal-slider compact">
              {products.map((product) => renderCategorySliderCard(product))}
            </div>
          </div>
        ))
      )}

      {activeCartItems.length > 0 ? (
        <div className="grocery-sticky-bottom-bar">
          <div className="grocery-sticky-total">
            <span>{cartCount} item(s)</span>
            <strong>{previewLoading ? 'Calculating...' : formatMoney(pricingPreview.finalTotal || cartEstimateTotal)}</strong>
          </div>

          <button
            type="button"
            className="grocery-sticky-btn"
            onClick={() => {
              if (!validateProductsStep()) return;
              setStep(STEP_REVIEW);
            }}
          >
            Review Cart
          </button>
        </div>
      ) : null}
    </div>
  );

  const renderCartItemRow = (item, readonly = false) => (
    <div key={item.productId || item.id || item.name} className="grocery-cart-item">
      <div className="grocery-cart-thumb">
        <img src={item.image || item.image_url || PLACEHOLDER_IMAGE} alt={item.name || item.product_name || 'Product'} />
      </div>

      <div className="grocery-cart-content">
        <div className="grocery-product-name">{item.name || item.product_name || 'Product'}</div>
        <div className="grocery-muted">
          {item.pack_size || ''} {item.unit_type ? `| ${item.unit_type}` : ''}
        </div>

        {!readonly ? (
          <div className="grocery-cart-mobile-tools">
            <div className="grocery-qty-controls cart-row-qty">
              <button className="grocery-qty-btn" type="button" onClick={() => decreaseQty({ id: item.productId })}>-</button>
              <span className="grocery-qty-display">{item.quantity || item.qty || 0}</span>
              <button className="grocery-qty-btn active" type="button" onClick={() => increaseQty({ id: item.productId })}>+</button>
            </div>

            <button type="button" className="grocery-remove-btn" onClick={() => removeFromCart(item.productId)}>
              Remove
            </button>
          </div>
        ) : (
          <div className="grocery-muted">x {item.quantity || item.qty || 0}</div>
        )}
      </div>

      {!readonly ? (
        <div className="grocery-cart-actions">
          <div className="grocery-qty-controls cart-row-qty">
            <button className="grocery-qty-btn" type="button" onClick={() => decreaseQty({ id: item.productId })}>-</button>
            <span className="grocery-qty-display">{item.quantity || item.qty || 0}</span>
            <button className="grocery-qty-btn active" type="button" onClick={() => increaseQty({ id: item.productId })}>+</button>
          </div>

          <button type="button" className="grocery-remove-btn" onClick={() => removeFromCart(item.productId)}>
            Remove
          </button>
        </div>
      ) : null}

      <div className={`grocery-cart-price ${isContactPriceItem(item) ? 'quote-price' : ''}`}>
        {getProductPriceLabel(item)}
      </div>
    </div>
  );

  const renderReviewStep = () => (
    <div className="grocery-qr-container">
      <div className="grocery-cart-card grocery-step-card">
        <div className="grocery-step-head">
          <div>
            <span className="grocery-step-badge">Step 4 / 5</span>
            <h3 className="grocery-section-title">Review Cart</h3>
          </div>
          <button className="grocery-mini-link-btn" onClick={() => setStep(STEP_PRODUCTS)}>
            Back
          </button>
        </div>

        <div className="grocery-summary-box grocery-customer-small-summary">
          <div className="grocery-review-item">
            <span>Mode</span>
            <strong>{orderMode === 'delivery' ? 'Delivery' : 'Takeaway'}</strong>
          </div>

          <div className="grocery-review-item">
            <span>Customer</span>
            <strong>{customerName}</strong>
          </div>

          <div className="grocery-review-item">
            <span>Mobile</span>
            <strong>{mobileNumber}</strong>
          </div>
        </div>

        {hasContactPriceItems ? (
          <div className="grocery-message grocery-compact-msg">
            Contact for Price product ka final amount vendor confirm karega. Online payment is order ke liye disabled rahega.
          </div>
        ) : null}

        <div className="grocery-cart-list">
          {activeCartItems.map((item) => renderCartItemRow(item))}
        </div>

        <div className="grocery-summary-box">
          <div className="grocery-review-item">
            <span>Base Total</span>
            <strong>{formatMoney(pricingPreview.baseTotal)}</strong>
          </div>

          <div className="grocery-review-item">
            <span>Extra Charges</span>
            <strong>{formatMoney(pricingPreview.extraCharges)}</strong>
          </div>

          <div className="grocery-review-item">
            <span>Offer Discount</span>
            <strong>- {formatMoney(pricingPreview.discountAmount)}</strong>
          </div>

          {pricingPreview.validatedOfferName ? (
            <div className="grocery-offer-note">
              Offer Applied: {pricingPreview.validatedOfferName}
            </div>
          ) : null}

          {hasContactPriceItems ? (
            <div className="grocery-offer-note">
              Kuch products ka price vendor confirm karega. Abhi payable total sirf fixed-price items ka hai.
            </div>
          ) : null}

          <div className="grocery-review-item grocery-total-row">
            <span>{hasContactPriceItems ? 'Current Total' : 'Grand Total'}</span>
            <strong>{formatMoney(pricingPreview.finalTotal)}</strong>
          </div>
        </div>

        <div className="grocery-actions grocery-inline-actions">
          <button className="grocery-action-button secondary small" onClick={() => setStep(STEP_PRODUCTS)}>
            Back
          </button>

          <button
            className="grocery-action-button small"
            onClick={() => {
              if (!validateProductsStep()) return;
              setStep(STEP_PAYMENT);
            }}
          >
            Next Payment
          </button>
        </div>
      </div>
    </div>
  );

  const renderPaymentStep = () => {
    const codAllowed = safeFlag(orderSettings.allow_cod, 1) === 1;
    const onlineAllowed = safeFlag(orderSettings.allow_online_payment, 1) === 1;
    const shopStatus = getShopOpenStatus(orderSettings);

    return (
      <div className="grocery-qr-container">
        <div className="grocery-card grocery-step-card">
          <div className="grocery-step-head">
            <div>
              <span className="grocery-step-badge">Step 5 / 5</span>
              <h3 className="grocery-section-title">Payment</h3>
            </div>
            <button className="grocery-mini-link-btn" onClick={() => setStep(STEP_REVIEW)}>
              Back
            </button>
          </div>

          <div className={`grocery-shop-status-box ${shopStatus.isOpen ? 'open' : 'closed'}`}>
            <span>Shop Status</span>
            <strong>{shopStatus.label}</strong>
            <p>{shopStatus.message}</p>
          </div>

          <div className="grocery-summary-box">
            <div className="grocery-review-item">
              <span>Grand Total</span>
              <strong>{formatMoney(pricingPreview.finalTotal)}</strong>
            </div>

            <div className="grocery-review-item">
              <span>Order Mode</span>
              <strong>{orderMode === 'delivery' ? 'Delivery' : 'Takeaway'}</strong>
            </div>

            <div className="grocery-review-item">
              <span>Shop Timing</span>
              <strong>{getShopTimingText(orderSettings)}</strong>
            </div>

            <div className="grocery-review-item">
              <span>Allowed Payment</span>
              <strong>
                {codAllowed && onlineAllowed
                  ? 'Cash / Online'
                  : onlineAllowed
                    ? 'Online Only'
                    : codAllowed
                      ? 'Cash Only'
                      : 'Not Available'}
              </strong>
            </div>
          </div>

          {!shopStatus.isOpen ? (
            <div className="grocery-error grocery-compact-msg">
              Shop closed hai. Abhi order place nahi ho sakta.
            </div>
          ) : null}

          {!codAllowed && onlineAllowed && shopStatus.isOpen ? (
            <div className="grocery-message grocery-compact-msg">
              Cash/COD बंद hai. Aap sirf Online Payment se order place kar sakte hain.
            </div>
          ) : null}

          {!onlineAllowed && codAllowed && shopStatus.isOpen ? (
            <div className="grocery-message grocery-compact-msg">
              Online Payment बंद hai. Aap Cash/COD se order place kar sakte hain.
            </div>
          ) : null}

          {hasContactPriceItems && shopStatus.isOpen ? (
            <div className="grocery-message grocery-compact-msg">
              Is order me Contact for Price product hai. Vendor final price confirm karega, isliye online payment disabled hai.
            </div>
          ) : null}

          {!codAllowed && !onlineAllowed ? (
            <div className="grocery-error grocery-compact-msg">
              Payment option available nahi hai. Order place nahi ho sakta.
            </div>
          ) : null}

          <div className="grocery-payment-grid">
            <button
              type="button"
              className={`grocery-payment-card ${paymentMode === 'cash' ? 'active' : ''}`}
              onClick={() => {
                if (!shopStatus.isOpen) {
                  setErrorMessage(shopStatus.message);
                  return;
                }

                if (!codAllowed && !hasContactPriceItems) {
                  setPaymentMode(onlineAllowed ? 'online' : 'cash');
                  setErrorMessage('Cash/COD allowed nahi hai. Online payment select karein.');
                  return;
                }

                clearMessages();
                setPaymentMode('cash');
              }}
              disabled={!shopStatus.isOpen || (!codAllowed && !hasContactPriceItems)}
            >
              <span>💵</span>
              <strong>{orderMode === 'delivery' ? 'Cash on Delivery' : 'Pay at Store'}</strong>
              <small>{codAllowed ? 'Order ke baad payment' : 'Cash/COD बंद hai'}</small>
            </button>

            <button
              type="button"
              className={`grocery-payment-card ${paymentMode === 'online' ? 'active' : ''}`}
              onClick={() => {
                if (!shopStatus.isOpen) {
                  setErrorMessage(shopStatus.message);
                  return;
                }

                if (hasContactPriceItems) {
                  setPaymentMode('cash');
                  setErrorMessage('Contact for Price item ke saath online payment allowed nahi hai. Vendor final price confirm karega.');
                  return;
                }

                if (!onlineAllowed) {
                  setErrorMessage('Online payment allowed nahi hai.');
                  return;
                }

                clearMessages();
                setPaymentMode('online');
              }}
              disabled={!shopStatus.isOpen || !onlineAllowed || hasContactPriceItems}
            >
              <span>💳</span>
              <strong>Online Payment</strong>
              <small>{onlineAllowed ? 'UPI / Card / Netbanking' : 'Online बंद hai'}</small>
            </button>
          </div>

          <div className="grocery-actions grocery-inline-actions">
            <button className="grocery-action-button secondary small" onClick={() => setStep(STEP_REVIEW)}>
              Back
            </button>

            <button
              className="grocery-action-button small"
              onClick={handlePlaceOrder}
              disabled={
                placingOrder ||
                paymentLoading ||
                isPaymentVerifying ||
                !shopStatus.isOpen ||
                (!hasContactPriceItems && !codAllowed && !onlineAllowed)
              }
            >
              {placingOrder || paymentLoading || isPaymentVerifying
                ? 'Processing...'
                : !shopStatus.isOpen
                  ? 'Shop Closed'
                  : hasContactPriceItems
                    ? 'Send Order'
                    : paymentMode === 'online'
                      ? `Pay ${formatMoney(pricingPreview.finalTotal)}`
                      : 'Place Order'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderOrderTrackingTimeline = (order) => {
    const currentStatus = getLatestTrackingStatus(order);
    const activeIndex = getTrackingIndex(currentStatus);
    const displayLogs = buildDisplayLogs(order);
    const isCancelled = currentStatus === 'cancelled';

    return (
      <div className={`grocery-tracking-box ${isCancelled ? 'cancelled' : ''}`}>
        <div className="grocery-tracking-head">
          <div>
            <span>Live Order Tracking</span>
            <strong>
              {isCancelled
                ? 'Order Cancelled'
                : TRACKING_STEPS[activeIndex]?.label || 'Order Placed'}
            </strong>
          </div>

          <div className={`grocery-current-status-pill ${isCancelled ? 'cancelled' : ''}`}>
            {isCancelled ? 'Cancelled' : normalizeStatusText(currentStatus)}
          </div>
        </div>

        {isCancelled ? (
          <div className="grocery-cancelled-note">
            Ye order cancel ho gaya hai. Details ke liye shop se contact karein.
          </div>
        ) : (
          <div className="grocery-progress-steps">
            {TRACKING_STEPS.map((trackStep, index) => {
              const isDone = index < activeIndex;
              const isActive = index === activeIndex;

              return (
                <div
                  key={trackStep.key}
                  className={`grocery-progress-step ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}
                >
                  <div className="grocery-progress-dot">
                    {isDone ? '✓' : index + 1}
                  </div>

                  <div className="grocery-progress-text">
                    <strong>{trackStep.shortLabel}</strong>
                    <span>{trackStep.help}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="grocery-status-log-box compact">
          <div className="grocery-section-head">
            <h3 className="grocery-section-title">Tracking History</h3>
            <span className="grocery-view-all">{displayLogs.length} update(s)</span>
          </div>

          <div className="grocery-status-log-list">
            {displayLogs.map((log) => (
              <div key={log.id} className="grocery-status-log-row">
                <div>
                  <span>{log.label}</span>
                  {log.note ? <p>{log.note}</p> : null}
                </div>
                <small>{log.created_at || '-'}</small>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderOrderDetailCard = (order, source = 'track') => {
    const products = getOrderProducts(order);
    const totalQty = products.reduce((sum, item) => sum + getProductQty(item), 0);
    const isSuccess = source === 'success';

    return (
      <div className={`grocery-order-detail-card ${isSuccess ? 'success-mode' : 'track-mode'}`}>
        <div className="grocery-order-detail-head">
          <div>
            <h2>{shopName}</h2>
            <p>Order Successful</p>
          </div>

          <span className="grocery-order-success-badge">✓</span>
        </div>

        {renderOrderTrackingTimeline(order)}

        <div className="grocery-order-info-grid">
          <div>
            <span>Total Price</span>
            <strong>{formatMoney(getOrderTotal(order))}</strong>
          </div>

          <div>
            <span>Customer</span>
            <strong>{getOrderCustomerName(order)}</strong>
          </div>

          <div>
            <span>Payment Status</span>
            <strong className={String(getOrderPaymentStatus(order)).toLowerCase() === 'unpaid' ? 'danger-text' : 'success-text'}>
              {normalizeStatusText(getOrderPaymentStatus(order))}
            </strong>
          </div>

          <div>
            <span>Payment Method</span>
            <strong>{normalizeStatusText(getOrderPaymentMethod(order))}</strong>
          </div>

          <div>
            <span>Token Number</span>
            <strong>{getOrderToken(order)}</strong>
          </div>

          <div>
            <span>Redeem Code</span>
            <strong>{getOrderRedeemCode(order)}</strong>
          </div>

          <div>
            <span>Expires on</span>
            <strong>{getOrderExpiry(order)}</strong>
          </div>

          <div>
            <span>Mode</span>
            <strong>{normalizeStatusText(getOrderMode(order))}</strong>
          </div>
        </div>

        {isSuccess ? (
          <>
            <div className="grocery-order-detail-actions">
              <button className="grocery-action-button secondary small" onClick={resetWholeFlow}>
                Place Another Order
              </button>

              <button className="grocery-action-button small" onClick={handleClaimNow}>
                Claim Now
              </button>
            </div>

            {showClaimInput ? (
              <div className="grocery-claim-box">
                <div className="grocery-form-group">
                  <label>Enter Redeem Code</label>
                  <input
                    type="text"
                    value={manualRedeemCode}
                    onChange={(event) => setManualRedeemCode(event.target.value.toUpperCase())}
                    placeholder="Redeem code"
                  />
                </div>

                <button className="grocery-action-button small" onClick={handleClaimRedeem}>
                  Submit
                </button>
              </div>
            ) : null}
          </>
        ) : null}

        <div className="grocery-selected-products-box">
          <div className="grocery-section-head">
            <h3 className="grocery-section-title">Selected Products:</h3>
            <span className="grocery-view-all">Total Qty: {totalQty}</span>
          </div>

          <div className="grocery-cart-list">
            {products.map((item, index) => (
              <div key={`${item.productId || item.id || item.name}-${index}`} className="grocery-track-product-card">
                <div className="grocery-cart-thumb">
                  <img src={item.image || item.image_url || PLACEHOLDER_IMAGE} alt={item.name || item.product_name || 'Product'} />
                </div>

                <div className="grocery-cart-content">
                  <div className="grocery-product-name">{item.name || item.product_name || 'Product'}</div>
                  <div className="grocery-muted">
                    {item.pack_size || ''} {item.unit_type ? `| ${item.unit_type}` : ''}
                  </div>
                  <div className="grocery-muted">x {getProductQty(item)}</div>
                </div>

                <div className="grocery-track-product-side">
                  <strong>{formatMoney(getProductLineTotal(item))}</strong>
                </div>
              </div>
            ))}
          </div>

          <div className="grocery-order-total-row">
            <span>Total Qty: {totalQty}</span>
            <strong>{formatMoney(getOrderTotal(order))}</strong>
          </div>
        </div>
      </div>
    );
  };

  const renderSuccessStep = () => (
    <div className="grocery-qr-container grocery-success-page">
      {placedOrder ? renderOrderDetailCard(placedOrder, 'success') : null}

      <div className="grocery-actions grocery-inline-actions">
        <button className="grocery-action-button small" onClick={resetWholeFlow}>
          New Order
        </button>

        <button
          className="grocery-action-button secondary small"
          onClick={() => {
            setStep(STEP_TRACK);
            setTrackMobile(normalizeMobile(mobileNumber));
          }}
        >
          Track Order
        </button>
      </div>
    </div>
  );

  const renderTrackStep = () => (
    <div className="grocery-qr-container grocery-track-page">
      <div className="grocery-card grocery-step-card">
        <div className="grocery-step-head">
          <div>
            <span className="grocery-step-badge">Track</span>
            <h3 className="grocery-section-title">Track Your Order</h3>
          </div>
          <button className="grocery-mini-link-btn" onClick={() => setStep(STEP_LANDING)}>
            Back
          </button>
        </div>

        <p className="grocery-muted">
          Mobile number dalein. Aaj ke order, payment, token, redeem code aur live tracking yahin dikhega.
        </p>

        <div className="grocery-track-search-row">
          <div className="grocery-form-group">
            <label>Mobile Number *</label>
            <input
              type="text"
              value={trackMobile}
              onChange={(event) => setTrackMobile(normalizeMobile(event.target.value))}
              placeholder="10 digit mobile"
              inputMode="numeric"
            />
          </div>

          <button
            className="grocery-action-button small"
            type="button"
            onClick={fetchTodayOrders}
            disabled={trackLoading}
          >
            {trackLoading ? 'Checking...' : 'Show Today Orders'}
          </button>
        </div>

        <div className="grocery-track-note-grid">
          <div>
            <strong>Today Only</strong>
            <span>Is mobile number ke sirf aaj ke orders show honge.</span>
          </div>

          <div>
            <strong>Live Tracking</strong>
            <span>Status logs se order progress dikhaya jayega.</span>
          </div>
        </div>

        {todayOrders.length > 0 ? (
          <div className="grocery-track-list">
            {todayOrders.map((order) => (
              <div key={getOrderId(order)} className="grocery-track-card">
                <div className="grocery-section-head">
                  <h3 className="grocery-section-title">Order #{getOrderId(order)}</h3>
                  <span className="grocery-view-all">{order.created_time || order.createdAt || ''}</span>
                </div>

                {renderOrderDetailCard(order, 'track')}
              </div>
            ))}
          </div>
        ) : null}

        <div className="grocery-actions grocery-inline-actions">
          <button className="grocery-action-button secondary small" onClick={() => setStep(STEP_LANDING)}>
            Back
          </button>

          <button
            className="grocery-action-button small"
            onClick={() => {
              setStep(STEP_MODE);
              setTodayOrders([]);
              setTrackMobile('');
            }}
          >
            New Booking
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div
        className="grocery-qr-wrapper"
        style={{
          backgroundImage: shopImage ? `url(${shopImage})` : 'none',
        }}
      >
        <div className="grocery-qr-container">
          <div className="grocery-loading-card">
            <h2>Loading grocery QR flow...</h2>
          </div>
        </div>
      </div>
    );
  }

  if (qrAccess.checked && !qrAccess.isActive) {
    return renderInactiveQrPage();
  }

  return (
    <div
      className="grocery-qr-wrapper"
      style={{
        backgroundImage: shopImage ? `url(${shopImage})` : 'none',
      }}
    >
      {renderHeader()}

      {step === STEP_LANDING && renderLandingStep()}
      {step === STEP_MODE && renderModeStep()}
      {step === STEP_CUSTOMER && renderCustomerStep()}
      {step === STEP_PRODUCTS && renderProductsStep()}
      {step === STEP_REVIEW && renderReviewStep()}
      {step === STEP_PAYMENT && renderPaymentStep()}
      {step === STEP_SUCCESS && renderSuccessStep()}
      {step === STEP_TRACK && renderTrackStep()}

      {renderVendorContactPopup()}
    </div>
  );
};

export default GroceryQrForm;
